import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import { useProjectContext } from "@/hooks/useProjectContext";
import { getToken } from "@/utils/auth";
import axios from "axios";
import Lucide from "@/components/Base/Lucide";
import Pagination from "@/components/Base/Pagination";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Table from "@/components/Base/Table";
import { Menu, Dialog } from "@/components/Base/Headless";
import Litepicker from "@/components/Base/Litepicker";
import Button from "@/components/Base/Button";
import dayjs from "dayjs";
import * as XLSX from 'xlsx';

type HeinekenPriceData = {
    id: string;
    createdAt: string;
    regionName: string | null;
    userId: string | null;
    productId: string | null;
    productName: string | null;
    productLine: string | null;
    opening: number | null;
    closing: number | null;
    qty: number | null;
    price: number | null;
    total: number | null;
    published: boolean;
    product?: {
        id: string;
        name: string;
        grossWeightPerUOM: number | null;
    } | null;
    outlet: {
        id: string;
        name: string;
        outletCategoryRel: {
            catName: string;
        } | null;
        outletGroupRel: {
            groupName: string;
        } | null;
    } | null;
    team: {
        id: string;
        name: string;
    } | null;
};

const FULL_FETCH_PAGE_SIZE = 2000;
const FULL_FETCH_CONCURRENCY = 3;

function applyDateRangeToParams(queryParams: URLSearchParams, dateRange: string) {
    if (!dateRange || !dateRange.trim()) return;

    const dateParts = dateRange.split(" - ").map(d => d.trim()).filter(d => d);
    if (dateParts.length < 1) return;

    const startDateStr = dateParts[0];
    const endDateStr = dateParts[1] || dateParts[0];
    const cleanStartDateStr = startDateStr.replace(/(\d+)(st|nd|rd|th)/g, '$1');
    const cleanEndDateStr = endDateStr.replace(/(\d+)(st|nd|rd|th)/g, '$1');

    let startDateParsed = dayjs(cleanStartDateStr, "DD MMM YYYY", true);
    let endDateParsed = dayjs(cleanEndDateStr, "DD MMM YYYY", true);
    if (!startDateParsed.isValid()) {
        startDateParsed = dayjs(cleanStartDateStr, "DD MMM YYYY");
    }
    if (!endDateParsed.isValid()) {
        endDateParsed = dayjs(cleanEndDateStr, "DD MMM YYYY");
    }

    if (startDateParsed.isValid()) {
        queryParams.append("startDate", startDateParsed.startOf('day').toISOString());
    }
    if (endDateParsed.isValid()) {
        queryParams.append("endDate", endDateParsed.endOf('day').toISOString());
    }
}

async function requestHeinekenPricePage(
    apiUrl: string,
    token: string,
    queryParams: URLSearchParams,
    page: number,
    pageSize: number,
    signal?: AbortSignal
): Promise<{ data: HeinekenPriceData[]; total: number; totalPages: number }> {
    const params = new URLSearchParams(queryParams);
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));

    const res = await fetch(`${apiUrl}/heineken-prices?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });
    if (!res.ok) {
        throw new Error(`Failed to fetch master data (page ${page})`);
    }

    const response = await res.json();
    return {
        data: Array.isArray(response?.data) ? response.data : [],
        total: Number(response?.total) || 0,
        totalPages: Number(response?.totalPages) || 1,
    };
}

function countGroupedRows(records: HeinekenPriceData[]): number {
    const groupKeys = new Set<string>();
    records.forEach((item) => {
        if (item.productLine === 'Competitor' || item.productLine === 'Premium') {
            return;
        }
        const dateKey = item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD') : '';
        groupKeys.add(`${item.userId || 'no-user'}_${dateKey}_${item.outlet?.id || 'no-outlet'}`);
    });
    return groupKeys.size;
}

function estimateGroupedRows(loadedRows: number, loadedProductLines: number, totalProductLines: number): number {
    if (loadedRows <= 0 || loadedProductLines <= 0) {
        return 0;
    }
    return Math.max(loadedRows, Math.round((totalProductLines * loadedRows) / loadedProductLines));
}

async function fetchAllHeinekenPrices(
    apiUrl: string,
    token: string,
    queryParams: URLSearchParams,
    options?: {
        signal?: AbortSignal;
        onProgress?: (loaded: number, total: number) => void;
    }
): Promise<{ records: HeinekenPriceData[]; total: number }> {
    const first = await requestHeinekenPricePage(
        apiUrl,
        token,
        queryParams,
        1,
        FULL_FETCH_PAGE_SIZE,
        options?.signal
    );
    const totalProductLines = first.total || first.data.length;
    const records: HeinekenPriceData[] = [...first.data];
    const firstRowCount = countGroupedRows(records);
    options?.onProgress?.(
        firstRowCount,
        estimateGroupedRows(firstRowCount, records.length, totalProductLines)
    );

    const totalPages = Math.max(1, Math.ceil(totalProductLines / FULL_FETCH_PAGE_SIZE));
    if (totalPages <= 1) {
        return { records, total: totalProductLines };
    }

    const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
    const pageResults = new Map<number, HeinekenPriceData[]>();
    let nextIndex = 0;

    const reportRowProgress = () => {
        const loadedRecords = [...records, ...Array.from(pageResults.values()).flat()];
        const loadedRows = countGroupedRows(loadedRecords);
        options?.onProgress?.(
            loadedRows,
            estimateGroupedRows(loadedRows, loadedRecords.length, totalProductLines)
        );
    };

    const runWorker = async () => {
        while (nextIndex < remainingPages.length) {
            const page = remainingPages[nextIndex++];
            const result = await requestHeinekenPricePage(
                apiUrl,
                token,
                queryParams,
                page,
                FULL_FETCH_PAGE_SIZE,
                options?.signal
            );
            pageResults.set(page, result.data);
            reportRowProgress();
        }
    };

    await Promise.all(
        Array.from({ length: Math.min(FULL_FETCH_CONCURRENCY, remainingPages.length) }, () => runWorker())
    );

    for (let page = 2; page <= totalPages; page++) {
        records.push(...(pageResults.get(page) || []));
    }

    const finalRowCount = countGroupedRows(records);
    options?.onProgress?.(finalRowCount, finalRowCount);
    return { records, total: totalProductLines };
}

export default function MasterData() {
    const { projectId } = useParams<{ projectId: string }>();
    const [searchParams] = useSearchParams();
    const { projectName } = useProjectContext();
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [dataLoading, setDataLoading] = useState(false);
    const [fetchProgress, setFetchProgress] = useState<{ loaded: number; total: number } | null>(null);
    const [refreshToken, setRefreshToken] = useState(0);
    const [data, setData] = useState<HeinekenPriceData[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Extract unique groups for filter options
    const availableGroups = useMemo(() => {
        const groupSet = new Set<string>();
        data.forEach(item => {
            const groupName = item.outlet?.outletGroupRel?.groupName;
            if (groupName) {
                groupSet.add(groupName);
            }
        });
        return Array.from(groupSet).sort();
    }, [data]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [searchQuery, setSearchQuery] = useState("");
    // Initialize dateRange from localStorage if available
    const [dateRange, setDateRange] = useState(() => {
        if (projectId) {
            const savedDateRange = localStorage.getItem(`masterData_dateRange_${projectId}`);
            return savedDateRange || "";
        }
        return "";
    });
    const [sortField, setSortField] = useState<'createdAt' | 'outlet' | 'team' | 'region' | 'category' | 'group' | 'dateByWeek'>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
    const [publishedFilter, setPublishedFilter] = useState<'unpublished' | 'published' | 'all'>('unpublished'); // Default to unpublished
    
    // Track edited values: Map<itemId_productId_field, {price?: number, qty?: number, total?: number, opening?: number, closing?: number}>
    // total is auto-calculated from qty × price
    const [editedValues, setEditedValues] = useState<Map<string, {price?: number, qty?: number, total?: number, opening?: number, closing?: number}>>(new Map());
    // Track raw input strings while user is typing (for price, qty, opening, and closing fields)
    const [rawInputValues, setRawInputValues] = useState<Map<string, string>>(new Map());
    // Track saved values: Map<itemId_productId_field, {price?: number, qty?: number, total?: number, opening?: number, closing?: number}>
    const [savedValues, setSavedValues] = useState<Map<string, {price?: number, qty?: number, total?: number, opening?: number, closing?: number}>>(new Map());
    const [isApplying, setIsApplying] = useState(false);
    
    // Adjustment modal state
    const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
    const [selectedProductForAdjustment, setSelectedProductForAdjustment] = useState<{id: string, name: string} | null>(null);
    const [adjustmentPercentage, setAdjustmentPercentage] = useState<string>('');
    
    // Publish modal state
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [unpublishRecordIds, setUnpublishRecordIds] = useState<string[]>([]);
    
    // Import Excel state
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({
        stage: '',
        percentage: 0,
    });
    const [showImportSummary, setShowImportSummary] = useState(false);
    const [importResult, setImportResult] = useState<{
        success: number;
        errors: number;
        warnings: number;
        errorDetails: Array<{ row: number; message: string }>;
        warningDetails: Array<{ row: number; message: string }>;
    } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Group data by userId, date, and outletId, then apply filters (defined after selectedGroups/selectedProducts)
    const groupedData = useMemo(() => {
        const groupMap = new Map<string, HeinekenPriceData[]>();
        
        data.forEach(item => {
            // Filter out competitor and premium products
            if (item.productLine === 'Competitor' || item.productLine === 'Premium') {
                return; // Skip competitor and premium products
            }
            
            // Apply group filter
            if (selectedGroups.length > 0) {
                const itemGroupName = item.outlet?.outletGroupRel?.groupName;
                if (!itemGroupName || !selectedGroups.includes(itemGroupName)) {
                    return; // Skip this item if group filter doesn't match
                }
            }

            // Apply product filter
            if (selectedProducts.length > 0) {
                if (!item.productId || !selectedProducts.includes(item.productId)) {
                    return; // Skip this item if product filter doesn't match
                }
            }
            
            // Create a unique key from userId, date (day only), and outletId
            const dateKey = item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD') : '';
            const groupKey = `${item.userId || 'no-user'}_${dateKey}_${item.outlet?.id || 'no-outlet'}`;
            
            if (!groupMap.has(groupKey)) {
                groupMap.set(groupKey, []);
            }
            groupMap.get(groupKey)!.push(item);
        });
        
        // Convert map to array of grouped items
        return Array.from(groupMap.values());
    }, [data, selectedGroups, selectedProducts]);

    // Calculate pagination based on grouped data (displayed rows)
    const displayedTotal = groupedData.length;
    const displayedTotalPages = useMemo(() => {
        return Math.ceil(displayedTotal / pageSize);
    }, [displayedTotal, pageSize]);

    // When we apply frontend filters (date range / group / product), we fetch a big dataset
    // and paginate/group on the frontend. Otherwise we rely on backend pagination.
    const useFrontendPagination = useMemo(() => {
        return Boolean((dateRange && dateRange.trim()) || selectedGroups.length > 0 || selectedProducts.length > 0);
    }, [dateRange, selectedGroups, selectedProducts]);

    const effectiveTotalPages = useMemo(() => {
        return useFrontendPagination ? displayedTotalPages : totalPages;
    }, [useFrontendPagination, displayedTotalPages, totalPages]);
    
    const displayedStartIndex = useMemo(() => {
        return (currentPage - 1) * pageSize;
    }, [currentPage, pageSize]);
    
    const displayedEndIndex = useMemo(() => {
        return Math.min(displayedStartIndex + pageSize, displayedTotal);
    }, [displayedStartIndex, pageSize, displayedTotal]);
    
    const paginatedGroupedData = useMemo(() => {
        return groupedData.slice(displayedStartIndex, displayedEndIndex);
    }, [groupedData, displayedStartIndex, displayedEndIndex]);

    // Helper function to get cell key for tracking edits
    const getCellKey = (itemId: string, productId: string, field: 'price' | 'qty' | 'total' | 'opening' | 'closing') => {
        return `${itemId}_${productId}_${field}`;
    };

    // Helper function to get current value (edited > saved > original)
    const getCellValue = (item: HeinekenPriceData, productId: string, field: 'price' | 'qty' | 'total' | 'opening' | 'closing'): number | null => {
        const cellKey = getCellKey(item.id, productId, field);
        
        // Check edited values first
        const edited = editedValues.get(cellKey);
        if (edited && edited[field] !== undefined) {
            return edited[field]!;
        }
        
        // Check saved values
        const saved = savedValues.get(cellKey);
        if (saved && saved[field] !== undefined) {
            return saved[field]!;
        }
        
        // Return original value
        if (item.productId === productId) {
            if (field === 'price') return item.price;
            if (field === 'qty') return item.qty;
            if (field === 'total') return item.total;
            if (field === 'opening') return item.opening;
            if (field === 'closing') return item.closing;
        }
        
        return null;
    };

    // Helper function to get display value for input (raw string if typing, otherwise formatted number)
    const getInputValue = (item: HeinekenPriceData, productId: string, field: 'price' | 'qty' | 'opening' | 'closing'): string => {
        const cellKey = getCellKey(item.id, productId, field);
        
        // If user is currently typing (raw input exists), show raw input (no formatting while typing)
        const rawInput = rawInputValues.get(cellKey);
        if (rawInput !== undefined) {
            // Always show raw input while user is typing - formatting happens on blur
            return rawInput;
        }
        
        // Otherwise, show formatted value (when not actively typing)
        const value = getCellValue(item, productId, field);
        if (value === null || value === undefined) {
            return '';
        }
        
        if (field === 'price') {
            return value.toFixed(2);
        } else if (field === 'qty' || field === 'opening' || field === 'closing') {
            return Math.round(value).toString();
        }
        
        return '';
    };

    // Helper function to calculate Sales (RM) from qty and price
    const calculateSalesRM = (item: HeinekenPriceData, productId: string): number | null => {
        const qty = getCellValue(item, productId, 'qty');
        const price = getCellValue(item, productId, 'price');
        
        if (qty !== null && qty !== undefined && price !== null && price !== undefined) {
            return qty * price;
        }
        
        return null;
    };

    // Helper function to check if cell is edited (yellow background)
    const isCellEdited = (itemId: string, productId: string, field: 'price' | 'qty' | 'total' | 'opening' | 'closing'): boolean => {
        const cellKey = getCellKey(itemId, productId, field);
        return editedValues.has(cellKey);
    };

    // Helper function to check if cell is saved (green background)
    const isCellSaved = (itemId: string, productId: string, field: 'price' | 'qty' | 'total' | 'opening' | 'closing'): boolean => {
        const cellKey = getCellKey(itemId, productId, field);
        return savedValues.has(cellKey);
    };

    // Handler for editing Price cell
    const handlePriceChange = (item: HeinekenPriceData, productId: string, newPrice: string) => {
        const cellKey = getCellKey(item.id, productId, 'price');
        
        // Store raw input string while user is typing (don't process/format here)
        const updatedRawInput = new Map(rawInputValues);
        if (newPrice === '') {
            updatedRawInput.delete(cellKey);
        } else {
            updatedRawInput.set(cellKey, newPrice);
        }
        setRawInputValues(updatedRawInput);
        
        // Try to parse the value
        const priceValue = newPrice === '' ? null : parseFloat(newPrice);
        
        // If it's a valid number, update the price and calculate total
        if (priceValue !== null && !isNaN(priceValue) && priceValue >= 0) {
            const currentEdited = editedValues.get(cellKey) || {};
            const updatedEdited = new Map(editedValues);
            updatedEdited.set(cellKey, { ...currentEdited, price: priceValue });
            
            // Auto-calculate Sales (RM) = qty * price
            const qty = getCellValue(item, productId, 'qty');
            if (qty !== null && qty !== undefined) {
                const newTotal = qty * priceValue;
                const totalCellKey = getCellKey(item.id, productId, 'total');
                updatedEdited.set(totalCellKey, { total: newTotal });
            }
            
            setEditedValues(updatedEdited);
        }
    };

    // Handler for Tab key - move from Sales (carton) to Price (same row), then to Sales (carton) in next row
    const handleTabKey = (e: React.KeyboardEvent<HTMLInputElement>, currentItemId: string, productId: string, field: 'qty' | 'price', direction: 'next' | 'prev' = 'next') => {
        if (e.key !== 'Tab') return;
        
        e.preventDefault();
        
        // Find current row index and current item
        const currentRowIndex = paginatedGroupedData.findIndex(group => 
            group.some(item => item.id === currentItemId && item.productId === productId)
        );
        
        if (currentRowIndex === -1) return;
        
        const currentGroup = paginatedGroupedData[currentRowIndex];
        const currentItem = currentGroup.find(item => item.id === currentItemId && item.productId === productId);
        
        if (!currentItem) return;
        
        let targetInputId: string | null = null;
        
        if (direction === 'next') {
            // Forward navigation
            if (field === 'qty') {
                // From Sales (carton) → move to Price in same row
                targetInputId = `input_${currentItemId}_${productId}_price`;
            } else if (field === 'price') {
                // From Price → move to Sales (carton) in next row
                const nextRowIndex = currentRowIndex + 1;
                if (nextRowIndex < paginatedGroupedData.length) {
                    const nextGroup = paginatedGroupedData[nextRowIndex];
                    const nextItem = nextGroup.find(item => item.productId === productId);
                    if (nextItem) {
                        targetInputId = `input_${nextItem.id}_${productId}_qty`;
                    }
                }
            }
        } else {
            // Reverse navigation (Shift+Tab)
            if (field === 'price') {
                // From Price → move to Sales (carton) in same row
                targetInputId = `input_${currentItemId}_${productId}_qty`;
            } else if (field === 'qty') {
                // From Sales (carton) → move to Price in previous row
                const prevRowIndex = currentRowIndex - 1;
                if (prevRowIndex >= 0) {
                    const prevGroup = paginatedGroupedData[prevRowIndex];
                    const prevItem = prevGroup.find(item => item.productId === productId);
                    if (prevItem) {
                        targetInputId = `input_${prevItem.id}_${productId}_price`;
                    }
                }
            }
        }
        
        if (targetInputId) {
            setTimeout(() => {
                const nextInput = document.getElementById(targetInputId!) as HTMLInputElement;
                if (nextInput) {
                    nextInput.focus();
                    nextInput.select();
                }
            }, 0);
        }
    };

    // Handler for editing Sales (carton) cell
    const handleSalesCartonChange = (item: HeinekenPriceData, productId: string, newQty: string) => {
        const cellKey = getCellKey(item.id, productId, 'qty');
        
        // Store raw input string while user is typing
        const updatedRawInput = new Map(rawInputValues);
        if (newQty === '') {
            updatedRawInput.delete(cellKey);
        } else {
            updatedRawInput.set(cellKey, newQty);
        }
        setRawInputValues(updatedRawInput);
        
        // Try to parse the value
        const qtyValue = newQty === '' ? null : parseFloat(newQty);
        
        // If it's a valid number, update the qty and calculate total
        if (qtyValue !== null && !isNaN(qtyValue) && qtyValue >= 0) {
            const currentEdited = editedValues.get(cellKey) || {};
            const updatedEdited = new Map(editedValues);
            updatedEdited.set(cellKey, { ...currentEdited, qty: qtyValue });
            
            // Auto-calculate Sales (RM) = qty * price
            const price = getCellValue(item, productId, 'price');
            if (price !== null && price !== undefined) {
                const newTotal = qtyValue * price;
                const totalCellKey = getCellKey(item.id, productId, 'total');
                updatedEdited.set(totalCellKey, { total: newTotal });
            }
            
            setEditedValues(updatedEdited);
        }
    };

    // Handler for editing Opening cell
    const handleOpeningChange = (item: HeinekenPriceData, productId: string, newOpening: string) => {
        const cellKey = getCellKey(item.id, productId, 'opening');
        
        // Store raw input string while user is typing
        const updatedRawInput = new Map(rawInputValues);
        if (newOpening === '') {
            updatedRawInput.delete(cellKey);
        } else {
            updatedRawInput.set(cellKey, newOpening);
        }
        setRawInputValues(updatedRawInput);
        
        // Try to parse the value
        const openingValue = newOpening === '' ? null : parseFloat(newOpening);
        
        // If it's a valid number, update the opening
        if (openingValue !== null && !isNaN(openingValue) && openingValue >= 0) {
            const currentEdited = editedValues.get(cellKey) || {};
            const updatedEdited = new Map(editedValues);
            updatedEdited.set(cellKey, { ...currentEdited, opening: openingValue });
            setEditedValues(updatedEdited);
        }
    };

    // Handler for editing Closing cell
    const handleClosingChange = (item: HeinekenPriceData, productId: string, newClosing: string) => {
        const cellKey = getCellKey(item.id, productId, 'closing');
        
        // Store raw input string while user is typing
        const updatedRawInput = new Map(rawInputValues);
        if (newClosing === '') {
            updatedRawInput.delete(cellKey);
        } else {
            updatedRawInput.set(cellKey, newClosing);
        }
        setRawInputValues(updatedRawInput);
        
        // Try to parse the value
        const closingValue = newClosing === '' ? null : parseFloat(newClosing);
        
        // If it's a valid number, update the closing
        if (closingValue !== null && !isNaN(closingValue) && closingValue >= 0) {
            const currentEdited = editedValues.get(cellKey) || {};
            const updatedEdited = new Map(editedValues);
            updatedEdited.set(cellKey, { ...currentEdited, closing: closingValue });
            setEditedValues(updatedEdited);
        }
    };

    // Count only direct user edits (price, qty, opening, or closing), excluding auto-calculated total
    const countDirectEdits = useMemo(() => {
        let count = 0;
        editedValues.forEach((value, cellKey) => {
            // Only count price, qty, opening, and closing edits, not auto-calculated total
            if (cellKey.endsWith('_price') && value.price !== undefined) {
                count++;
            } else if (cellKey.endsWith('_qty') && value.qty !== undefined) {
                count++;
            } else if (cellKey.endsWith('_opening') && value.opening !== undefined) {
                count++;
            } else if (cellKey.endsWith('_closing') && value.closing !== undefined) {
                count++;
            }
        });
        return count;
    }, [editedValues]);

    // Handler for Reset button - reset all edited values back to original
    const handleResetChanges = () => {
        if (countDirectEdits === 0) return;
        
        // Clear all edited values and raw input values
        setEditedValues(new Map());
        setRawInputValues(new Map());
    };

    // Handler for Apply button
    const handleApplyChanges = async () => {
        if (countDirectEdits === 0) return;

        setIsApplying(true);
        try {
            // Group edits by item ID for batch updates
            const updatesByItem = new Map<string, {price?: number, qty?: number, total?: number, opening?: number, closing?: number, productId: string}[]>();
            
            editedValues.forEach((value, cellKey) => {
                const [itemId, productId, field] = cellKey.split('_');
                if (!updatesByItem.has(itemId)) {
                    updatesByItem.set(itemId, []);
                }
                const itemUpdates = updatesByItem.get(itemId)!;
                const existingUpdate = itemUpdates.find(u => u.productId === productId);
                if (existingUpdate) {
                    if (field === 'price') existingUpdate.price = value.price;
                    if (field === 'qty') existingUpdate.qty = value.qty;
                    if (field === 'total') existingUpdate.total = value.total;
                    if (field === 'opening') existingUpdate.opening = value.opening;
                    if (field === 'closing') existingUpdate.closing = value.closing;
                } else {
                    const update: any = { productId };
                    if (field === 'price' && value.price !== undefined) update.price = value.price;
                    if (field === 'qty' && value.qty !== undefined) update.qty = value.qty;
                    if (field === 'total' && value.total !== undefined) update.total = value.total;
                    if (field === 'opening' && value.opening !== undefined) update.opening = value.opening;
                    if (field === 'closing' && value.closing !== undefined) update.closing = value.closing;
                    itemUpdates.push(update);
                }
            });

            // Update data state with saved values
            const updatedData = [...data];
            const newSavedValues = new Map(savedValues);
            
            editedValues.forEach((value, cellKey) => {
                const [itemId, productId, field] = cellKey.split('_');
                const itemIndex = updatedData.findIndex(item => item.id === itemId && item.productId === productId);
                
                if (itemIndex !== -1) {
                    if (field === 'price' && value.price !== undefined) {
                        updatedData[itemIndex].price = value.price;
                    } else if (field === 'qty' && value.qty !== undefined) {
                        updatedData[itemIndex].qty = value.qty;
                    } else if (field === 'total' && value.total !== undefined) {
                        updatedData[itemIndex].total = value.total;
                    } else if (field === 'opening' && value.opening !== undefined) {
                        updatedData[itemIndex].opening = value.opening;
                    } else if (field === 'closing' && value.closing !== undefined) {
                        updatedData[itemIndex].closing = value.closing;
                    }
                }
                
                // Move to saved values
                newSavedValues.set(cellKey, value);
            });

            // Make API call to save changes to backend
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();
            
            // Create array of updates - each edited cell becomes an update
            const updates: Array<{id: string, updateData: any}> = [];
            
            editedValues.forEach((value, cellKey) => {
                const [itemId, productId, field] = cellKey.split('_');
                
                // Find the item in data to verify it exists
                const item = data.find(d => d.id === itemId && d.productId === productId);
                if (!item) return; // Skip if item not found
                
                // Check if we already have an update for this item
                let existingUpdate = updates.find(u => u.id === itemId);
                
                if (!existingUpdate) {
                    existingUpdate = { id: itemId, updateData: {} };
                    updates.push(existingUpdate);
                }
                
                // Add the field to update
                if (field === 'price' && value.price !== undefined) {
                    existingUpdate.updateData.price = value.price;
                } else if (field === 'qty' && value.qty !== undefined) {
                    existingUpdate.updateData.qty = value.qty;
                } else if (field === 'total' && value.total !== undefined) {
                    existingUpdate.updateData.total = value.total;
                } else if (field === 'opening' && value.opening !== undefined) {
                    existingUpdate.updateData.opening = value.opening;
                } else if (field === 'closing' && value.closing !== undefined) {
                    existingUpdate.updateData.closing = value.closing;
                }
            });

            // Send updates to backend
            if (updates.length > 0) {
                await Promise.all(updates.map(async (update) => {
                    if (!update.id || Object.keys(update.updateData).length === 0) return;
                    
                    try {
                        await axios.put(
                            `${apiUrl}/heineken-prices/${update.id}`,
                            update.updateData,
                            {
                                headers: { Authorization: `Bearer ${token}` }
                            }
                        );
                    } catch (error: any) {
                        console.error(`Failed to update record ${update.id}:`, error);
                        throw error; // Re-throw to be caught by outer try-catch
                    }
                }));
            }

            setData(updatedData);
            setSavedValues(newSavedValues);
            setEditedValues(new Map()); // Clear edited values
            
            // Show success message
            const successMessage = `Successfully saved ${countDirectEdits} change${countDirectEdits > 1 ? 's' : ''}`;
            console.log(successMessage);
            
            // Clear saved values after 3 seconds (remove green highlight)
            setTimeout(() => {
                setSavedValues(new Map());
            }, 3000);
        } catch (error) {
            console.error('Failed to apply changes:', error);
            window.alert('Error: Failed to save changes. Please try again.');
        } finally {
            setIsApplying(false);
        }
    };

    // Calculate total Sales (RM) per product from paginated data (using edited/saved values or calculated)
    const totalSalesRMByProduct = useMemo(() => {
        const totals = new Map<string, number>();
        paginatedGroupedData.forEach(group => {
            group.forEach(item => {
                if (item.productId) {
                    // Check if qty is null - if so, skip this item (don't add to total)
                    const qty = getCellValue(item, item.productId, 'qty');
                    if (qty === null || qty === undefined) {
                        return; // Skip items with null qty
                    }
                    
                    // Use calculated Sales (RM) if available, otherwise use stored total
                    const calculatedTotal = calculateSalesRM(item, item.productId);
                    const total = calculatedTotal !== null ? calculatedTotal : getCellValue(item, item.productId, 'total');
                    if (total !== null && total !== undefined) {
                        const currentTotal = totals.get(item.productId) || 0;
                        totals.set(item.productId, currentTotal + total);
                    }
                }
            });
        });
        return totals;
    }, [paginatedGroupedData, editedValues, savedValues]);


    // Get date range and current total for a product (from all data, not just paginated)
    const getProductAdjustmentInfo = useCallback((productId: string) => {
        const productItems = data.filter(item => item.productId === productId);
        if (productItems.length === 0) {
            return { dateRange: '', currentTotal: 0, currentTotalQty: 0 };
        }

        // Get date range
        const validDates = productItems
            .map(item => item.createdAt ? dayjs(item.createdAt) : null)
            .filter((d): d is dayjs.Dayjs => d !== null);
        
        if (validDates.length === 0) {
            return { dateRange: '', currentTotal: 0, currentTotalQty: 0 };
        }

        // Find min and max dates manually
        let minDate = validDates[0];
        let maxDate = validDates[0];
        validDates.forEach(date => {
            if (date.isBefore(minDate)) {
                minDate = date;
            }
            if (date.isAfter(maxDate)) {
                maxDate = date;
            }
        });

        const dateRange = minDate && maxDate 
            ? `${minDate.format('DD MMM YYYY')} - ${maxDate.format('DD MMM YYYY')}`
            : '';

        // Calculate current total Sales (RM) and total quantity (using edited/saved values if available)
        let currentTotal = 0;
        let currentTotalQty = 0;
        productItems.forEach(item => {
            const qty = getCellValue(item, productId, 'qty');
            
            // Skip items with null qty - they shouldn't contribute to total
            if (qty === null || qty === undefined) {
                return;
            }
            
            const calculatedTotal = calculateSalesRM(item, productId);
            const total = calculatedTotal !== null ? calculatedTotal : getCellValue(item, productId, 'total');
            if (total !== null && total !== undefined) {
                currentTotal += total;
            }
                currentTotalQty += qty;
        });

        return { dateRange, currentTotal, currentTotalQty };
    }, [data, editedValues, savedValues]);

    // Handle click on Total Sales (RM) cell
    const handleTotalSalesRMClick = (productId: string) => {
        const product = uniqueProducts.find(p => p.id === productId);
        if (product) {
            setSelectedProductForAdjustment({ id: productId, name: product.name });
            setAdjustmentPercentage('');
            setShowAdjustmentModal(true);
        }
    };

    // Calculate adjusted total sales based on adjusted quantity
    const adjustedTotalSales = useMemo(() => {
        if (!selectedProductForAdjustment || !adjustmentPercentage || adjustmentPercentage.trim() === '') return null;
        
        const adjustment = parseFloat(adjustmentPercentage);
        if (isNaN(adjustment)) return null;
        
        // Get all items for this product (from all data, not just paginated)
        const productId = selectedProductForAdjustment.id;
        const productItems = data.filter(item => item.productId === productId);
        
        if (productItems.length === 0) return null;
        
        let adjustedTotal = 0;
        
        productItems.forEach(item => {
            // Get current Sales (carton) quantity (edited/saved or original)
            const currentQty = getCellValue(item, productId, 'qty');
            
            // Skip if quantity is null or undefined
            if (currentQty === null || currentQty === undefined) {
                return;
            }
            
            // Get current Price
            const currentPrice = getCellValue(item, productId, 'price');
            
            // Skip if price is null, undefined, or zero
            if (currentPrice === null || currentPrice === undefined || currentPrice === 0) {
                return;
            }
            
            // Step 1: Increase Sales (carton) quantity by adjustment percentage
            // Example: If currentQty = 100 and adjustment = 10%, then adjustedQty = 100 * 1.10 = 110
            const adjustedQty = Math.round(currentQty * (1 + adjustment / 100));
            
            // Step 2: Calculate Adjusted Total Sales = adjusted quantity × price
            // Example: If adjustedQty = 110 and price = 50, then adjustedItemTotal = 110 × 50 = 5500
            const adjustedItemTotal = adjustedQty * currentPrice;
            
            // Sum up all adjusted totals
            adjustedTotal += adjustedItemTotal;
        });
        
        return adjustedTotal;
    }, [selectedProductForAdjustment, adjustmentPercentage, data, editedValues, savedValues]);

    // Count unpublished rows (groups) in current view - count groups that have at least one unpublished item
    const unpublishedCount = useMemo(() => {
        return paginatedGroupedData.filter(group => {
            // A group is considered unpublished if at least one item in the group is unpublished
            return group.some(item => !item.published);
        }).length;
    }, [paginatedGroupedData]);

    // Handle publish action
    const handlePublish = async () => {
        setIsPublishing(true);
        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            // Get current filter criteria
            const publishData: any = {};
            if (projectId) publishData.projectId = projectId;
            if (companyId) publishData.companyId = companyId;
            if (selectedGroups.length > 0) publishData.selectedGroups = selectedGroups;
            if (selectedProducts.length > 0) publishData.selectedProducts = selectedProducts;
            if (dateRange && dateRange.trim()) {
                const dateParts = dateRange.split(" - ").map(d => d.trim()).filter(d => d);
                if (dateParts.length >= 1) {
                    const startDateStr = dateParts[0].replace(/(\d+)(st|nd|rd|th)/g, '$1');
                    const endDateStr = (dateParts[1] || dateParts[0]).replace(/(\d+)(st|nd|rd|th)/g, '$1');
                    const startDateParsed = dayjs(startDateStr, "DD MMM YYYY", true);
                    const endDateParsed = dayjs(endDateStr, "DD MMM YYYY", true);
                    if (startDateParsed.isValid()) {
                        publishData.startDate = startDateParsed.startOf('day').toISOString();
                    }
                    if (endDateParsed.isValid()) {
                        publishData.endDate = endDateParsed.endOf('day').toISOString();
                    }
                }
            }

            const response = await axios.post(
                `${apiUrl}/heineken-prices/publish`,
                publishData,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setRefreshToken((value) => value + 1);
                setShowPublishModal(false);
                alert(`Successfully published ${response.data.data.count} record(s)`);
            }
        } catch (error: any) {
            console.error('Failed to publish:', error);
            alert(`Failed to publish: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsPublishing(false);
        }
    };

    // Handle unpublish action
    const handleUnpublish = async (recordIds: string[]) => {
        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            const response = await axios.post(
                `${apiUrl}/heineken-prices/unpublish`,
                { recordIds },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setRefreshToken((value) => value + 1);
                setUnpublishRecordIds([]);
                alert(`Successfully unpublished ${response.data.data.count} record(s)`);
            }
        } catch (error: any) {
            console.error('Failed to unpublish:', error);
            alert(`Failed to unpublish: ${error.response?.data?.message || error.message}`);
        }
    };

    // Apply adjustment to all rows for the selected product
    const handleApplyAdjustment = async () => {
        if (!selectedProductForAdjustment || !adjustmentPercentage) return;
        
        const adjustment = parseFloat(adjustmentPercentage);
        if (isNaN(adjustment)) return;

        setIsApplying(true);
        try {
            // Get all items for this product (from all data, not just paginated)
            const productItems = data.filter(item => item.productId === selectedProductForAdjustment.id);
            
            // Calculate updates for each item
            const updates: Array<{id: string, qty: number, total: number}> = [];
            
            productItems.forEach(item => {
                // Get current values (edited/saved or original)
                const currentPrice = getCellValue(item, item.productId!, 'price');
                const currentQty = getCellValue(item, item.productId!, 'qty');
                
                if (currentPrice === null || currentPrice === undefined || currentPrice === 0) {
                    return; // Skip if no price
                }
                
                if (currentQty === null || currentQty === undefined) {
                    return; // Skip if no quantity
                }
                
                // Calculate new quantity with adjustment (round to whole number)
                const newQty = Math.round(currentQty * (1 + adjustment / 100));
                
                // Calculate new total from adjusted quantity
                const newTotal = newQty * currentPrice;
                
                updates.push({
                    id: item.id,
                    qty: newQty,
                    total: newTotal
                });
            });

            // Update local data state
            const updatedData = [...data];
            updates.forEach(update => {
                const itemIndex = updatedData.findIndex(item => item.id === update.id);
                if (itemIndex !== -1) {
                    updatedData[itemIndex].qty = update.qty;
                    updatedData[itemIndex].total = update.total;
                }
            });
            setData(updatedData);

            // Clear any existing edits for this product
            const updatedEditedValues = new Map(editedValues);
            productItems.forEach(item => {
                const qtyKey = getCellKey(item.id, item.productId!, 'qty');
                const totalKey = getCellKey(item.id, item.productId!, 'total');
                updatedEditedValues.delete(qtyKey);
                updatedEditedValues.delete(totalKey);
            });
            setEditedValues(updatedEditedValues);

            // Save to backend
            await Promise.all(updates.map(async (update) => {
                try {
                    await axios.put(
                        `${import.meta.env.VITE_API_URL}/heineken-prices/${update.id}`,
                        { qty: update.qty, total: update.total },
                        {
                            headers: { Authorization: `Bearer ${getToken()}` }
                        }
                    );
                } catch (error) {
                    console.error(`Failed to update item ${update.id}:`, error);
                }
            }));

            // Mark adjusted cells as saved to show green highlight
            const newSavedValues = new Map(savedValues);
            updates.forEach(update => {
                const item = productItems.find(i => i.id === update.id);
                if (item) {
                    const qtyKey = getCellKey(item.id, item.productId!, 'qty');
                    const totalKey = getCellKey(item.id, item.productId!, 'total');
                    newSavedValues.set(qtyKey, { qty: update.qty });
                    newSavedValues.set(totalKey, { total: update.total });
                }
            });
            setSavedValues(newSavedValues);

            // Clear saved values after 3 seconds to remove green highlight
            setTimeout(() => {
                setSavedValues(new Map());
            }, 3000);

            // Close modal
            setShowAdjustmentModal(false);
            setSelectedProductForAdjustment(null);
            setAdjustmentPercentage('');
        } catch (error) {
            console.error('Failed to apply adjustment:', error);
        } finally {
            setIsApplying(false);
        }
    };

    // Load column widths from localStorage or use defaults
    const getStoredColumnWidths = (): { [key: string]: number } => {
        const stored = localStorage.getItem('masterDataColumnWidths');
        let widths: { [key: string]: number } = {};
        if (stored) {
            try {
                widths = JSON.parse(stored);
                // Increase all product column widths by 50% when loading from localStorage
                // Opening: increase to 30px if < 30px, Others: increase to 60px if < 60px
                Object.keys(widths).forEach(key => {
                    if (key.includes('_opening') && widths[key] < 30) {
                        widths[key] = 30;
                    } else if (key.includes('_sales_rm') && widths[key] < 78) {
                        widths[key] = 78;
                    } else if ((key.includes('_closing') || key.includes('_sales_carton') || 
                               key.includes('_hector_litre') || key.includes('_price')) && widths[key] < 60) {
                        widths[key] = 60;
                    }
                });
            } catch (e) {
                console.error('Failed to parse stored column widths:', e);
            }
        }
        // Merge with defaults
        return {
            published: 100, // Published status column
            date: 120,
            dateByWeek: 120,
            region: 100,
            category: 120,
            group: 100,
            outlet: 150,
            team: 120,
            ...widths,
        };
    };

    const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(getStoredColumnWidths());
    const [resizing, setResizing] = useState<string | null>(null);
    const [resizeStartX, setResizeStartX] = useState(0);
    const [resizeStartWidth, setResizeStartWidth] = useState(0);
    const [uniqueProducts, setUniqueProducts] = useState<Array<{ id: string; name: string; grossWeightPerUOM: number | null; displayOrder: number | null; createdAt: string }>>([]);

    // Filter uniqueProducts based on selectedProducts - only show selected products in table columns
    // Defined early so it can be used in other useMemo/useCallback hooks
    const filteredUniqueProducts = useMemo(() => {
        if (selectedProducts.length === 0) {
            // If no products selected, show all products
            return uniqueProducts;
        }
        // Only show selected products
        return uniqueProducts.filter(product => selectedProducts.includes(product.id));
    }, [uniqueProducts, selectedProducts]);

    // Format date functions
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const formatDateByWeek = (dateString: string) => {
        const date = new Date(dateString);
        // Calculate week number in the year (week starts on Monday)
        const yearStart = new Date(date.getFullYear(), 0, 1);
        const dayOfYear = Math.floor((date.getTime() - yearStart.getTime()) / (24 * 60 * 60 * 1000));
        // Adjust for week starting on Monday (0 = Monday, 6 = Sunday)
        const dayOfWeek = (date.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0
        const weekNumber = Math.ceil((dayOfYear + dayOfWeek + 1) / 7);
        return `Week ${weekNumber}`;
    };

    // Export to Excel function
    const handleExportToExcel = useCallback(() => {
        // Build column headers for two-row header structure
        // First row: Static columns + Product names (spanning 6 columns each)
        const firstRowHeaders: (string | null)[] = [];
        // Static columns
        firstRowHeaders.push('Date');
        firstRowHeaders.push('Date by Week');
        firstRowHeaders.push('Region');
        firstRowHeaders.push('Category');
        firstRowHeaders.push('Group');
        firstRowHeaders.push('Outlet');
        firstRowHeaders.push('Team');
        
        // Product names (will span 6 columns each) - use filtered products
        filteredUniqueProducts.forEach(product => {
            firstRowHeaders.push(product.name); // Product name
            firstRowHeaders.push(null, null, null, null, null); // 5 more nulls to span 6 columns total
        });
        
        // Second row: Empty for static columns + Sub-column names
        const secondRowHeaders: (string | null)[] = [];
        // Empty cells for static columns
        for (let i = 0; i < 7; i++) {
            secondRowHeaders.push(null);
        }
        
        // Sub-column names for each product - use filtered products
        const subColumns = ['Opening', 'Closing', 'Sales (carton)', 'Hecto Litre', 'Price', 'Sales (RM)'];
        filteredUniqueProducts.forEach(() => {
            subColumns.forEach(subCol => {
                secondRowHeaders.push(subCol);
            });
        });
        
        // Build data rows from groupedData
        const dataRows = groupedData.map(group => {
            const firstItem = group[0];
            const row: any[] = [];
            
            // Static columns
            row.push(firstItem.createdAt ? formatDate(firstItem.createdAt) : '-');
            row.push(firstItem.createdAt ? formatDateByWeek(firstItem.createdAt) : '-');
            row.push(firstItem.regionName || '-');
            row.push(firstItem.outlet?.outletCategoryRel?.catName || '-');
            row.push(firstItem.outlet?.outletGroupRel?.groupName || '-');
            row.push(firstItem.outlet?.name || '-');
            row.push(firstItem.team?.name || '-');
            
            // Create a map of productId -> item for quick lookup
            const productMap = new Map<string, HeinekenPriceData>();
            group.forEach(item => {
                if (item.productId) {
                    productMap.set(item.productId, item);
                }
            });
            
            // Product columns
            filteredUniqueProducts.forEach(product => {
                const productItem = productMap.get(product.id);
                row.push(productItem && productItem.opening !== null && productItem.opening !== undefined 
                    ? Math.round(productItem.opening) 
                    : '-');
                row.push(productItem && productItem.closing !== null && productItem.closing !== undefined 
                    ? Math.round(productItem.closing) 
                    : '-');
                row.push(productItem && productItem.qty !== null && productItem.qty !== undefined 
                    ? Math.round(productItem.qty) 
                    : '-');
                // Calculate Hecto Litre: grossWeightPerUOM * Sales (carton)
                const productData = filteredUniqueProducts.find(p => p.id === product.id);
                const grossWeightPerUOM = productData?.grossWeightPerUOM;
                const salesCarton = productItem && productItem.qty !== null && productItem.qty !== undefined ? productItem.qty : null;
                if (salesCarton !== null && grossWeightPerUOM !== null && grossWeightPerUOM !== undefined) {
                    row.push(parseFloat((salesCarton * grossWeightPerUOM).toFixed(4)));
                } else {
                    row.push('-');
                }
                row.push(productItem && productItem.price !== null && productItem.price !== undefined 
                    ? parseFloat(productItem.price.toFixed(2)) 
                    : '-');
                row.push(productItem && productItem.total !== null && productItem.total !== undefined 
                    ? parseFloat(productItem.total.toFixed(2)) 
                    : '-');
            });
            
            return row;
        });
        
        // Combine header rows and data rows
        const allRows = [firstRowHeaders, secondRowHeaders, ...dataRows];
        
        // Create worksheet from array of arrays
        const worksheet = XLSX.utils.aoa_to_sheet(allRows);
        
        // Merge cells for product names in first row (spanning 6 columns each)
        const staticColsCount = 7;
        filteredUniqueProducts.forEach((product, productIndex) => {
            const startCol = staticColsCount + (productIndex * 6);
            const endCol = startCol + 5; // Span 6 columns (0-5)
            
            if (!worksheet['!merges']) {
                worksheet['!merges'] = [];
            }
            worksheet['!merges'].push({
                s: { r: 0, c: startCol }, // Start at row 0, column startCol
                e: { r: 0, c: endCol }   // End at row 0, column endCol
            });
        });
        
        // Merge cells for static columns in first row (spanning 2 rows)
        for (let i = 0; i < staticColsCount; i++) {
            if (!worksheet['!merges']) {
                worksheet['!merges'] = [];
            }
            worksheet['!merges'].push({
                s: { r: 0, c: i }, // Start at row 0, column i
                e: { r: 1, c: i }  // End at row 1, column i
            });
        }
        
        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Master Data');
        
        // Generate filename with current date
        const date = dayjs().format('YYYY-MM-DD');
        const filename = `master_data_${date}.xlsx`;
        
        // Download file
        XLSX.writeFile(workbook, filename);
    }, [groupedData, filteredUniqueProducts]);

    // Handle Excel import
    const handleImportExcel = useCallback(async (file: File) => {
        setIsImporting(true);
        setImportProgress({ stage: 'Reading Excel file...', percentage: 10 });
        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            // Read Excel file
            setImportProgress({ stage: 'Reading Excel file...', percentage: 15 });
            const arrayBuffer = await file.arrayBuffer();
            setImportProgress({ stage: 'Parsing Excel data...', percentage: 30 });
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: null }) as any[][];

            if (jsonData.length < 3) {
                throw new Error('Excel file must have at least 3 rows (2 header rows + 1 data row)');
            }

            // Parse headers (first 2 rows)
            const firstRow = jsonData[0] as (string | null)[];
            const secondRow = jsonData[1] as (string | null)[];

            // Find column indices
            const staticCols = ['Date', 'Date by Week', 'Region', 'Category', 'Group', 'Outlet', 'Team'];
            const productCols = ['Opening', 'Closing', 'Sales (carton)', 'Hecto Litre', 'Price', 'Sales (RM)'];
            
            // Map static columns
            const colIndices: any = {
                date: firstRow.indexOf('Date'),
                dateByWeek: firstRow.indexOf('Date by Week'),
                region: firstRow.indexOf('Region'),
                category: firstRow.indexOf('Category'),
                group: firstRow.indexOf('Group'),
                outlet: firstRow.indexOf('Outlet'),
                team: firstRow.indexOf('Team'),
            };

            // Find product columns - products are in first row, sub-columns in second row
            const productMap = new Map<string, { startCol: number; name: string }>();
            let currentCol = staticCols.length;
            while (currentCol < firstRow.length) {
                const productName = firstRow[currentCol];
                if (productName && typeof productName === 'string' && productName.trim()) {
                    // Find this product in uniqueProducts
                    const product = uniqueProducts.find(p => p.name === productName.trim());
                    if (product) {
                        productMap.set(product.id, { startCol: currentCol, name: productName.trim() });
                    }
                    currentCol += 6; // Each product spans 6 columns
                } else {
                    currentCol++;
                }
            }

            // Parse data rows (starting from row 3, index 2)
            setImportProgress({ stage: 'Processing data rows...', percentage: 40 });
            const updates: any[] = [];
            const errors: Array<{ row: number; message: string }> = [];
            const totalRows = jsonData.length - 2; // Exclude header rows

            for (let rowIndex = 2; rowIndex < jsonData.length; rowIndex++) {
                // Update progress during parsing
                if (rowIndex % 10 === 0 || rowIndex === jsonData.length - 1) {
                    const parseProgress = 40 + Math.floor(((rowIndex - 2) / totalRows) * 10);
                    setImportProgress({ stage: `Processing row ${rowIndex - 1} of ${jsonData.length - 2}...`, percentage: parseProgress });
                }
                const row = jsonData[rowIndex];
                const excelRowNumber = rowIndex + 1; // Excel row number (1-based)

                try {
                    // Extract static fields
                    const dateStr = row[colIndices.date];
                    const outletName = row[colIndices.outlet];
                    const teamName = row[colIndices.team];

                    if (!dateStr || !outletName) {
                        errors.push({ row: excelRowNumber, message: 'Missing required fields: Date or Outlet' });
                        continue;
                    }

                    // Parse date
                    const date = dayjs(dateStr, ['DD MMM YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY'], true);
                    if (!date.isValid()) {
                        errors.push({ row: excelRowNumber, message: `Invalid date format: ${dateStr}` });
                        continue;
                    }

                    // For each product in the row
                    for (const [productId, productInfo] of productMap.entries()) {
                        const opening = row[productInfo.startCol];
                        const closing = row[productInfo.startCol + 1];
                        const salesCarton = row[productInfo.startCol + 2];
                        const hectoLitre = row[productInfo.startCol + 3];
                        const price = row[productInfo.startCol + 4];
                        const salesRM = row[productInfo.startCol + 5];

                        // Skip if all product fields are empty
                        if (opening === null && closing === null && salesCarton === null && price === null && salesRM === null) {
                            continue;
                        }

                        // Parse numbers - handle hyphens, empty strings, and invalid values
                        const parseNumber = (value: any): number | null => {
                            if (value === null || value === undefined || value === '' || value === '-') {
                                return null;
                            }
                            const parsed = parseFloat(value);
                            return isNaN(parsed) ? null : parsed;
                        };
                        
                        let numOpening = parseNumber(opening);
                        let numClosing = parseNumber(closing);
                        let numSalesCarton = parseNumber(salesCarton);
                        let numHectoLitre = parseNumber(hectoLitre);
                        let numPrice = parseNumber(price);
                        let numSalesRM = parseNumber(salesRM);

                        // Auto-calculate Sales (RM) = Sales (carton) × Price
                        // Always calculate if both Sales (carton) and Price are provided (overrides Excel value)
                        if (numSalesCarton !== null && numPrice !== null) {
                            numSalesRM = numSalesCarton * numPrice;
                        }

                        // Auto-calculate Hecto Litre = Sales (carton) × grossWeightPerUOM
                        // Always calculate if Sales (carton) is provided (overrides Excel value)
                        if (numSalesCarton !== null) {
                            const productData = uniqueProducts.find(p => p.id === productId);
                            const grossWeightPerUOM = productData?.grossWeightPerUOM;
                            if (grossWeightPerUOM !== null && grossWeightPerUOM !== undefined) {
                                numHectoLitre = numSalesCarton * grossWeightPerUOM;
                            }
                        }

                        // Add to updates (only if at least one field has a value)
                        // Note: null values are explicitly sent to allow clearing fields in database
                        if (numOpening !== null || numClosing !== null || numSalesCarton !== null || 
                            numPrice !== null || numSalesRM !== null || numHectoLitre !== null) {
                            updates.push({
                                excelRowNumber: excelRowNumber, // Include Excel row number for counting unique rows
                                date: date.format('YYYY-MM-DD'),
                                outletName: outletName.toString().trim(),
                                teamName: teamName ? teamName.toString().trim() : null,
                                productId,
                                projectId: projectId || null, // Include projectId for precise matching
                                // Explicitly send null for empty cells (not undefined) to allow clearing fields
                                opening: numOpening ?? null,
                                closing: numClosing ?? null,
                                qty: numSalesCarton ?? null,
                                hectoLitre: numHectoLitre ?? null,
                                price: numPrice ?? null,
                                total: numSalesRM ?? null,
                            });
                        }
                    }
                } catch (error: any) {
                    errors.push({ row: excelRowNumber, message: error.message || 'Error parsing row' });
                }
            }

            if (updates.length === 0 && errors.length === 0) {
                alert('No data found in Excel file to import.');
                setIsImporting(false);
                return;
            }

            // Send to backend
            setImportProgress({ stage: 'Uploading to server...', percentage: 60 });
            const response = await axios.post(
                `${apiUrl}/heineken-prices/batch-update`,
                { updates },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            const uploadProgress = 60 + Math.floor((progressEvent.loaded / progressEvent.total) * 20);
                            setImportProgress({ 
                                stage: `Uploading... ${Math.floor((progressEvent.loaded / progressEvent.total) * 100)}%`, 
                                percentage: uploadProgress 
                            });
                        }
                    }
                }
            );
            
            setImportProgress({ stage: 'Processing on server...', percentage: 85 });

            if (response.data.success) {
                const result = response.data.data;
                // Separate errors from warnings (published records)
                const allMessages = [...errors, ...(result.errors || [])];
                const errorDetails: Array<{ row: number; message: string }> = [];
                const warningDetails: Array<{ row: number; message: string }> = [];
                
                allMessages.forEach(msg => {
                    if (msg.message.includes('Skipped published record') || msg.message.includes('Published records cannot be updated')) {
                        warningDetails.push(msg);
                    } else {
                        errorDetails.push(msg);
                    }
                });
                
                setImportResult({
                    success: result.successCount || 0,
                    errors: errorDetails.length,
                    warnings: warningDetails.length,
                    errorDetails,
                    warningDetails,
                });
                setShowImportSummary(true);
                setImportProgress({ stage: 'Refreshing data...', percentage: 95 });
                setRefreshToken((value) => value + 1);
                setImportProgress({ stage: 'Complete!', percentage: 100 });
            }
        } catch (error: any) {
            console.error('Failed to import Excel:', error);
            setImportProgress({ stage: 'Error occurred', percentage: 0 });
            alert(`Failed to import Excel: ${error.response?.data?.message || error.message}`);
        } finally {
            // Small delay to show completion before closing
            setTimeout(() => {
                setIsImporting(false);
                setImportProgress({ stage: '', percentage: 0 });
            }, 500);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [uniqueProducts, projectId, companyId, publishedFilter, sortField, sortOrder]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleImportExcel(file);
        }
    };

    // Extract products for filter options (defined after uniqueProducts)
    const availableProductsForFilter = useMemo(() => {
        return uniqueProducts.map(p => ({ id: p.id, name: p.name }));
    }, [uniqueProducts]);

    // Calculate total Hecto Litre by product (from paginated data)
    const totalHectoLitreByProduct = useMemo(() => {
        const totals = new Map<string, number>();
        paginatedGroupedData.forEach(group => {
            group.forEach(item => {
                if (item.productId) {
                    const productData = uniqueProducts.find(p => p.id === item.productId);
                    const grossWeightPerUOM = productData?.grossWeightPerUOM;
                    const qty = getCellValue(item, item.productId, 'qty');
                    
                    if (qty !== null && qty !== undefined && grossWeightPerUOM !== null && grossWeightPerUOM !== undefined) {
                        const hectoLitre = qty * grossWeightPerUOM;
                        const currentTotal = totals.get(item.productId) || 0;
                        totals.set(item.productId, currentTotal + hectoLitre);
                    }
                }
            });
        });
        return totals;
    }, [paginatedGroupedData, uniqueProducts, editedValues, savedValues]);
    
    // Available columns that can be frozen (all columns before Opening)
    const availableColumns = [
        { key: 'date', label: 'Date' },
        { key: 'dateByWeek', label: 'Date by Week' },
        { key: 'region', label: 'Region' },
        { key: 'category', label: 'Category' },
        { key: 'group', label: 'Group' },
        { key: 'outlet', label: 'Outlet' },
        { key: 'team', label: 'Team' },
    ];
    
    // Load frozen columns from localStorage or use all by default
    const getStoredFrozenColumns = (): string[] => {
        const stored = localStorage.getItem('masterDataFrozenColumns');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                // Validate that all stored columns are still valid
                return parsed.filter((col: string) => availableColumns.some(ac => ac.key === col));
            } catch (e) {
                console.error('Failed to parse stored frozen columns:', e);
            }
        }
        // Default: freeze all columns
        return availableColumns.map(col => col.key);
    };
    
    const [frozenColumns, setFrozenColumns] = useState<string[]>(getStoredFrozenColumns());
    
    // Save frozen columns to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('masterDataFrozenColumns', JSON.stringify(frozenColumns));
    }, [frozenColumns]);

    // Save date range to localStorage whenever it changes
    useEffect(() => {
        if (projectId) {
            if (dateRange) {
                localStorage.setItem(`masterData_dateRange_${projectId}`, dateRange);
            } else {
                // Remove from localStorage if date range is cleared
                localStorage.removeItem(`masterData_dateRange_${projectId}`);
            }
        }
    }, [dateRange, projectId]);

    // Save column widths to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('masterDataColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);
    
    // Increase all product column widths by 50% on component mount and when products change
    useEffect(() => {
        setColumnWidths(prev => {
            const updated = { ...prev };
            let hasChanges = false;
            Object.keys(updated).forEach(key => {
                if (key.includes('_opening')) {
                    if (updated[key] < 18) {
                        updated[key] = 18;
                        hasChanges = true;
                    } else if (updated[key] > 18) {
                        updated[key] = Math.round(updated[key] * 0.6);
                        hasChanges = true;
                    }
                } else if (key.includes('_sales_rm') && updated[key] < 78) {
                    updated[key] = 78;
                    hasChanges = true;
                } else if ((key.includes('_closing') || key.includes('_sales_carton'))) {
                    if (updated[key] < 36) {
                        updated[key] = 36;
                        hasChanges = true;
                    } else if (updated[key] > 36) {
                        updated[key] = Math.round(updated[key] * 0.6);
                        hasChanges = true;
                    }
                } else if ((key.includes('_hector_litre') || key.includes('_price')) && updated[key] < 60) {
                    updated[key] = 60;
                    hasChanges = true;
                }
            });
            return hasChanges ? updated : prev;
        });
    }, [uniqueProducts]);

    // Fetch project to get companyId
    useEffect(() => {
        const fetchProject = async () => {
            if (!projectId) {
                setLoading(false);
                return;
            }

            try {
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const token = getToken();
                
                const response = await axios.get(`${apiUrl}/projects/${projectId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                const project = response.data.data || response.data;
                if (project?.companyId) {
                    setCompanyId(project.companyId);
                } else {
                    // Fallback to URL param if project doesn't have companyId
                    const urlCompanyId = searchParams.get("companyId");
                    setCompanyId(urlCompanyId);
                }
            } catch (error) {
                console.error("Failed to fetch project:", error);
                // Fallback to URL param
                const urlCompanyId = searchParams.get("companyId");
                setCompanyId(urlCompanyId);
            } finally {
                setLoading(false);
            }
        };

        fetchProject();
    }, [projectId, searchParams]);

    const backendPageKey = useFrontendPagination ? 'full' : `${currentPage}:${pageSize}`;

    // Fetch HeinekenPrice data
    useEffect(() => {
        if (!projectId && !companyId) return;

        const abortController = new AbortController();
        let cancelled = false;

        const fetchData = async () => {
            try {
                setDataLoading(true);
                setFetchProgress(null);
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const token = getToken();

                const queryParams = new URLSearchParams();
                if (projectId) queryParams.append("projectId", projectId);
                if (companyId) queryParams.append("companyId", companyId);
                if (searchQuery) queryParams.append("q", searchQuery);
                applyDateRangeToParams(queryParams, dateRange);

                queryParams.append("sortBy", sortField);
                queryParams.append("sortOrder", sortOrder);
                
                // Add published filter
                if (publishedFilter === 'published') {
                    queryParams.append("published", "true");
                } else if (publishedFilter === 'unpublished') {
                    queryParams.append("published", "false");
                }
                // If 'all', don't add the filter (show all)

                let fetchedData: HeinekenPriceData[] = [];

                if (useFrontendPagination) {
                    const { records } = await fetchAllHeinekenPrices(apiUrl, token || '', queryParams, {
                        signal: abortController.signal,
                        onProgress: (loaded, totalCount) => {
                            if (!cancelled) {
                                setFetchProgress({ loaded, total: totalCount });
                            }
                        },
                    });
                    fetchedData = records;
                    setData(fetchedData);
                    setTotalPages(1);
                    setTotal(0);
                } else {
                    const response = await requestHeinekenPricePage(
                        apiUrl,
                        token || '',
                        queryParams,
                        currentPage,
                        pageSize,
                        abortController.signal
                    );
                    fetchedData = response.data;
                    setData(fetchedData);
                    setTotalPages(response.totalPages || 1);
                    setTotal(response.total || 0);
                }

                if (cancelled) return;
                
                // If no data and we have filters, ensure we show empty state
                if (fetchedData.length === 0 && searchQuery) {
                    setUniqueProducts([]);
                }
                
                // Extract unique product IDs from fetched data (excluding competitor and premium products)
                const productIds = new Set<string>();
                fetchedData
                    .filter((item): item is HeinekenPriceData => 
                        item.productId !== null && 
                        item.productName !== null &&
                        item.productLine !== 'Competitor' &&
                        item.productLine !== 'Premium'
                    )
                    .forEach(item => {
                        if (item.productId) {
                            productIds.add(item.productId);
                        }
                    });

                // Fetch products with displayOrder and createdAt to sort them properly
                let productsWithOrder: Array<{ id: string; name: string; grossWeightPerUOM: number | null; displayOrder: number | null; createdAt: string }> = [];
                
                if (productIds.size > 0) {
                    try {
                        const productQueryParams = new URLSearchParams();
                        if (companyId) productQueryParams.append("companyId", companyId);
                        if (projectId) productQueryParams.append("projectId", projectId);
                        productQueryParams.append("page", "1");
                        productQueryParams.append("pageSize", "1000"); // Get all products

                        const productsRes = await fetch(
                            `${apiUrl}/products?${productQueryParams.toString()}`,
                            {
                                headers: { Authorization: `Bearer ${token}` }
                            }
                        );
                        const productsData = await productsRes.json();
                        const allProducts: any[] = Array.isArray(productsData?.data) ? productsData.data : [];

                        // Create a map of productId -> product data for quick lookup
                        const productDataMap = new Map<string, { id: string; name: string; grossWeightPerUOM: number | null; displayOrder: number | null; createdAt: string }>();
                        
                        // Get product data from fetched HeinekenPrice data
                        fetchedData.forEach(item => {
                            if (item.productId && item.productName && 
                                item.productLine !== 'Competitor' && item.productLine !== 'Premium') {
                                if (!productDataMap.has(item.productId)) {
                                    // Find the product in allProducts to get displayOrder and createdAt
                                    const productInfo = allProducts.find(p => p.id === item.productId);
                                    productDataMap.set(item.productId, {
                                        id: item.productId,
                                        name: item.productName,
                                        grossWeightPerUOM: item.product?.grossWeightPerUOM ?? null,
                                        displayOrder: productInfo?.displayOrder ?? null,
                                        createdAt: productInfo?.createdAt ?? new Date().toISOString()
                                    });
                                }
                            }
                        });

                        productsWithOrder = Array.from(productDataMap.values());
                        
                        // Sort products using the same logic as Products/index.tsx
                        // 1. Products with displayOrder come first, sorted by displayOrder ascending
                        // 2. Products without displayOrder come after, sorted by createdAt descending (newest first)
                        productsWithOrder.sort((a, b) => {
                            const aHasOrder = a.displayOrder !== null && a.displayOrder !== undefined;
                            const bHasOrder = b.displayOrder !== null && b.displayOrder !== undefined;

                            if (aHasOrder && bHasOrder) {
                                // Both have displayOrder - sort by displayOrder ascending
                                return a.displayOrder! - b.displayOrder!;
                            } else if (aHasOrder && !bHasOrder) {
                                // a has order, b doesn't - a comes first
                                return -1;
                            } else if (!aHasOrder && bHasOrder) {
                                // b has order, a doesn't - b comes first
                                return 1;
                            } else {
                                // Neither has displayOrder - sort by createdAt descending (newest first)
                                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                            }
                        });
                    } catch (err) {
                        console.error("Failed to fetch products for sorting:", err);
                        // Fallback: use products from fetchedData without displayOrder/createdAt
                        const productMap = new Map<string, { id: string; name: string; grossWeightPerUOM: number | null; displayOrder: number | null; createdAt: string }>();
                        fetchedData
                            .filter((item): item is HeinekenPriceData => 
                                item.productId !== null && 
                                item.productName !== null &&
                                item.productLine !== 'Competitor' &&
                                item.productLine !== 'Premium'
                            )
                            .forEach(item => {
                                if (item.productId && item.productName) {
                                    productMap.set(item.productId, { 
                                        id: item.productId, 
                                        name: item.productName,
                                        grossWeightPerUOM: item.product?.grossWeightPerUOM ?? null,
                                        displayOrder: null,
                                        createdAt: new Date().toISOString()
                                    });
                                }
                            });
                        productsWithOrder = Array.from(productMap.values())
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    }
                }
                
                setUniqueProducts(productsWithOrder);
                
                // Calculate column widths based on content length
                const calculateColumnWidth = (text: string, minWidth: number = 30, padding: number = 16): number => {
                    // Estimate: approximately 7-8 pixels per character for text-xs font
                    const charWidth = 7.5;
                    const textWidth = text.length * charWidth;
                    return Math.max(minWidth, textWidth + padding);
                };

                // Calculate max width for each product column based on data
                setColumnWidths(prev => {
                    const updated = { ...prev };
                    productsWithOrder.forEach(product => {
                        const productId = product.id;
                        
                        // Get all items for this product
                        const productItems = fetchedData.filter(item => item.productId === product.id);
                        
                        // Calculate widths for each column type
                        const columnConfigs = [
                            {
                                key: 'opening',
                                header: 'Opening',
                                getValue: (item: HeinekenPriceData) => item.opening !== null && item.opening !== undefined 
                                    ? Math.round(item.opening).toString() 
                                    : '-',
                                minWidth: 18
                            },
                            {
                                key: 'closing',
                                header: 'Closing',
                                getValue: (item: HeinekenPriceData) => item.closing !== null && item.closing !== undefined 
                                    ? Math.round(item.closing).toString() 
                                    : '-',
                                minWidth: 36
                            },
                            {
                                key: 'sales_carton',
                                header: 'Sales (carton)',
                                getValue: (item: HeinekenPriceData) => item.qty !== null && item.qty !== undefined 
                                    ? Math.round(item.qty).toString() 
                                    : '-',
                                minWidth: 36
                            },
                            {
                                key: 'hector_litre',
                                header: 'Hecto Litre',
                                getValue: () => '-',
                                minWidth: 40
                            },
                            {
                                key: 'price',
                                header: 'Price',
                                getValue: (item: HeinekenPriceData) => item.price !== null && item.price !== undefined 
                                    ? item.price.toFixed(2) 
                                    : '-',
                                minWidth: 40
                            },
                            {
                                key: 'sales_rm',
                                header: 'Sales (RM)',
                                getValue: (item: HeinekenPriceData) => item.total !== null && item.total !== undefined 
                                    ? item.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    : '-',
                                minWidth: 50
                            }
                        ];
                        
                        columnConfigs.forEach(config => {
                            const columnKey = `product_${productId}_${config.key}`;
                            
                            // Calculate max width from header and all data values
                            let maxWidth = calculateColumnWidth(config.header, config.minWidth);
                            
                            productItems.forEach(item => {
                                const value = config.getValue(item);
                                const valueWidth = calculateColumnWidth(value, config.minWidth);
                                maxWidth = Math.max(maxWidth, valueWidth);
                            });
                            
                            // Only update if the calculated width is larger than current, or if not set
                            if (!updated[columnKey] || updated[columnKey] < maxWidth) {
                                updated[columnKey] = maxWidth;
                            }
                        });
                    });
                    return updated;
                });
            } catch (err: any) {
                if (err?.name === 'AbortError') return;
                console.error("Failed to fetch data:", err);
            } finally {
                if (!cancelled) {
                    setDataLoading(false);
                    setFetchProgress(null);
                }
            }
        };

        fetchData();
        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [backendPageKey, searchQuery, dateRange, sortField, sortOrder, projectId, companyId, publishedFilter, useFrontendPagination, refreshToken]);
    
    // Reset to page 1 when date range changes
    const prevDateRangeRef = useRef<string>(dateRange);
    useEffect(() => {
        if (prevDateRangeRef.current !== dateRange) {
            prevDateRangeRef.current = dateRange;
            setCurrentPage(1);
        }
    }, [dateRange]);
    
    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedGroups, selectedProducts, publishedFilter]);
    
    // Ensure currentPage doesn't exceed displayedTotalPages after filtering or page size change
    useEffect(() => {
        if (currentPage > effectiveTotalPages && effectiveTotalPages > 0) {
            setCurrentPage(1);
        }
    }, [currentPage, effectiveTotalPages]);

    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setCurrentPage(1);
    };

    const handleSort = (field: 'createdAt' | 'outlet' | 'team' | 'region' | 'category' | 'group' | 'dateByWeek') => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < effectiveTotalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleFirstPage = () => {
        setCurrentPage(1);
    };

    const handleLastPage = () => {
        setCurrentPage(effectiveTotalPages);
    };

    const handleResizeStart = (columnKey: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setResizing(columnKey);
        setResizeStartX(e.clientX);
        // Use appropriate default based on column type
        let defaultWidth = 120;
        if (columnKey.startsWith('product_')) {
            if (columnKey.includes('_opening')) {
                defaultWidth = 18;
            } else if (columnKey.includes('_closing') || columnKey.includes('_sales_carton')) {
                defaultWidth = 36;
            } else if (columnKey.includes('_sales_rm')) {
                defaultWidth = 78;
            } else {
                defaultWidth = 60;
            }
        }
        setResizeStartWidth(columnWidths[columnKey] || defaultWidth);
    };

    const handleResizeMove = useCallback((e: MouseEvent) => {
        if (!resizing) return;
        
        const diff = e.clientX - resizeStartX;
        // Use appropriate minimum: 18px for opening columns, 36px for closing/sales_carton, 40px for other product columns, 60px for other columns
        let minWidth = 60;
        if (resizing!.startsWith('product_')) {
            if (resizing!.includes('_opening')) {
                minWidth = 18;
            } else if (resizing!.includes('_closing') || resizing!.includes('_sales_carton')) {
                minWidth = 36;
            } else {
                minWidth = 40;
            }
        }
        const newWidth = Math.max(minWidth, resizeStartWidth + diff);
        
        setColumnWidths(prev => ({
            ...prev,
            [resizing!]: newWidth
        }));
    }, [resizing, resizeStartX, resizeStartWidth]);

    const handleResizeEnd = useCallback(() => {
        setResizing(null);
    }, []);

    useEffect(() => {
        if (resizing) {
            document.addEventListener('mousemove', handleResizeMove);
            document.addEventListener('mouseup', handleResizeEnd);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            
            return () => {
                document.removeEventListener('mousemove', handleResizeMove);
                document.removeEventListener('mouseup', handleResizeEnd);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
        }
    }, [resizing, handleResizeMove, handleResizeEnd]);

    // Calculate left positions for frozen columns (only selected columns)
    const frozenColumnPositions = useMemo(() => {
        const positions: { [key: string]: number } = {};
        const paddingLeft = 16; // Padding from the left edge of the container
        let currentLeft = paddingLeft;
        
        // Calculate positions only for frozen columns in order
        availableColumns.forEach(col => {
            if (frozenColumns.includes(col.key)) {
                positions[col.key] = currentLeft;
                currentLeft += columnWidths[col.key] || 
                    (col.key === 'date' ? 120 :
                     col.key === 'dateByWeek' ? 120 :
                     col.key === 'region' ? 100 :
                     col.key === 'category' ? 120 :
                     col.key === 'group' ? 100 :
                     col.key === 'outlet' ? 150 :
                     col.key === 'team' ? 120 : 120);
            }
        });
        
        return positions;
    }, [columnWidths, frozenColumns]);
    
    const toggleFrozenColumn = (columnKey: string) => {
        setFrozenColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(key => key !== columnKey);
            } else {
                // Insert in the correct order based on availableColumns
                const newFrozen = [...prev];
                const columnIndex = availableColumns.findIndex(col => col.key === columnKey);
                const insertIndex = newFrozen.findIndex(key => {
                    const keyIndex = availableColumns.findIndex(col => col.key === key);
                    return keyIndex > columnIndex;
                });
                if (insertIndex === -1) {
                    newFrozen.push(columnKey);
                } else {
                    newFrozen.splice(insertIndex, 0, columnKey);
                }
                return newFrozen;
            }
        });
    };
    
    // Helper function to get sticky style for a column
    const getStickyStyle = (columnKey: string, baseStyle: React.CSSProperties, isHeader: boolean = false): React.CSSProperties => {
        if (frozenColumns.includes(columnKey)) {
            return {
                ...baseStyle,
                position: 'sticky',
                left: frozenColumnPositions[columnKey],
                zIndex: isHeader ? 50 : 20,
                boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)'
            };
        }
        return baseStyle;
    };

    // Calculate product header width (sum of 6 sub-columns)
    const getProductHeaderWidth = useCallback((productId: string): number => {
        return (columnWidths[`product_${productId}_opening`] || 18) +
               (columnWidths[`product_${productId}_closing`] || 36) +
               (columnWidths[`product_${productId}_sales_carton`] || 36) +
               (columnWidths[`product_${productId}_hector_litre`] || 60) +
               (columnWidths[`product_${productId}_price`] || 60) +
               (columnWidths[`product_${productId}_sales_rm`] || 78);
    }, [columnWidths]);

    // Calculate total table width based on all column widths
    const totalTableWidth = useMemo(() => {
        let total = 0;
        // Static columns
        total += columnWidths.date || 120;
        total += columnWidths.dateByWeek || 120;
        total += columnWidths.region || 100;
        total += columnWidths.category || 120;
        total += columnWidths.group || 100;
        total += columnWidths.outlet || 150;
        total += columnWidths.team || 120;
        
        // Product columns (6 sub-columns per product) - use filtered products
        filteredUniqueProducts.forEach(product => {
            total += getProductHeaderWidth(product.id);
        });
        
        return total;
    }, [columnWidths, filteredUniqueProducts, getProductHeaderWidth]);

    const fetchProgressPercent = fetchProgress && fetchProgress.total > 0
        ? Math.min(100, Math.round((fetchProgress.loaded / fetchProgress.total) * 100))
        : 0;

    if (loading) {
        return (
            <ProtectedRoute>
                <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                            <div>Loading data...</div>
                        </div>
                    </div>
                </RoleGate>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <style>{`
                    .freeze-columns-menu [data-headlessui-state] > div.absolute.z-30,
                    .freeze-columns-menu [data-headlessui-state] > div[class*="absolute"][class*="z-30"] {
                        z-index: 9999 !important;
                    }
                `}</style>
                <div style={{ width: '100vw', maxWidth: '100vw', marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)', paddingLeft: '10rem', paddingRight: '10rem' }}>
                    <div style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}>
                        <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                {projectName ? `${projectName} - Master Data` : `Master Data`}
                                {(useFrontendPagination ? displayedTotal : total) > 0 && (
                                    <span className="ml-2 text-sm font-normal text-white">
                                        ({useFrontendPagination ? displayedTotal : total} {(useFrontendPagination ? displayedTotal : total) === 1 ? 'record' : 'records'})
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-3 md:ml-auto relative freeze-columns-menu" style={{ zIndex: 9999 }}>
                                <div className="relative inline-flex">
                                    <Lucide
                                        icon="Calendar"
                                        className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                    />
                                    <Litepicker
                                        value={dateRange}
                                        onChange={(e) => {
                                            const newDateRange = (e.target?.value || "").trim();
                                            setDateRange(newDateRange);
                                        }}
                                        options={{
                                            autoApply: true,
                                            singleMode: false,
                                            numberOfColumns: 2,
                                            numberOfMonths: 2,
                                            showWeekNumbers: true,
                                            format: "DD MMM YYYY",
                                            dropdowns: {
                                                minYear: 1990,
                                                maxYear: null,
                                                months: true,
                                                years: true,
                                            },
                                        }}
                                        className="w-fit min-w-[280px] max-w-[400px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
                                    />
                                </div>
                                <Menu className="relative" style={{ zIndex: 9999 }}>
                                    <Menu.Button className="relative inline-flex min-w-[100px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm whitespace-nowrap">
                                        <Lucide
                                            icon="Filter"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        Filter
                                        {(selectedGroups.length > 0 || selectedProducts.length > 0) && (
                                            <span className="ml-1 text-xs">({selectedGroups.length + selectedProducts.length})</span>
                                        )}
                                    </Menu.Button>
                                    <Menu.Items className="w-80" style={{ zIndex: 9999 }}>
                                        <div className="p-4">
                                            <div className="text-sm font-medium mb-4">Filter Options</div>
                                            
                                            {/* Group Filter */}
                                            <div className="mb-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Group</div>
                                                    {availableGroups.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                                            onClick={() => {
                                                                const allSelected = availableGroups.every(g => selectedGroups.includes(g));
                                                                if (allSelected) {
                                                                    setSelectedGroups([]);
                                                                } else {
                                                                    setSelectedGroups([...availableGroups]);
                                                                }
                                                            }}
                                                        >
                                                            {availableGroups.every(g => selectedGroups.includes(g)) ? 'Deselect all' : 'Select all'}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                                    {availableGroups.length === 0 ? (
                                                        <div className="text-xs text-slate-400">No groups available</div>
                                                    ) : (
                                                        availableGroups.map((groupName) => (
                                                            <label key={groupName} className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input mr-2"
                                                                    checked={selectedGroups.includes(groupName)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedGroups([...selectedGroups, groupName]);
                                                                        } else {
                                                                            setSelectedGroups(selectedGroups.filter(g => g !== groupName));
                                                                        }
                                                                    }}
                                                                />
                                                                <span className="text-xs">{groupName}</span>
                                                            </label>
                                                        ))
                                                    )}
                                                </div>
                                            </div>

                                            {/* Published Status Filter */}
                                            <div className="mb-4">
                                                <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Published Status</div>
                                                <FormSelect
                                                    value={publishedFilter}
                                                    onChange={(e) => setPublishedFilter(e.target.value as 'unpublished' | 'published' | 'all')}
                                                    className="w-full text-xs"
                                                >
                                                    <option value="unpublished">Unpublished</option>
                                                    <option value="published">Published</option>
                                                    <option value="all">All</option>
                                                </FormSelect>
                                            </div>

                                            {/* Product Filter */}
                                            <div className="mb-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Product Name</div>
                                                    {availableProductsForFilter.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                                            onClick={() => {
                                                                const allSelected = availableProductsForFilter.every(p => selectedProducts.includes(p.id));
                                                                if (allSelected) {
                                                                    setSelectedProducts([]);
                                                                } else {
                                                                    setSelectedProducts(availableProductsForFilter.map(p => p.id));
                                                                }
                                                            }}
                                                        >
                                                            {availableProductsForFilter.every(p => selectedProducts.includes(p.id)) ? 'Deselect all' : 'Select all'}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                                    {availableProductsForFilter.length === 0 ? (
                                                        <div className="text-xs text-slate-400">No products available</div>
                                                    ) : (
                                                        availableProductsForFilter.map((product) => (
                                                            <label key={product.id} className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input mr-2"
                                                                    checked={selectedProducts.includes(product.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedProducts([...selectedProducts, product.id]);
                                                                        } else {
                                                                            setSelectedProducts(selectedProducts.filter(p => p !== product.id));
                                                                        }
                                                                    }}
                                                                />
                                                                <span className="text-xs">{product.name}</span>
                                                            </label>
                                                        ))
                                                    )}
                                                </div>
                                            </div>

                                            {/* Clear Filters Button */}
                                            {(selectedGroups.length > 0 || selectedProducts.length > 0 || publishedFilter !== 'unpublished') && (
                                                <button
                                                    type="button"
                                                    className="w-full mt-2 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-800 hover:bg-slate-300 dark:hover:bg-darkmode-700 rounded"
                                                    onClick={() => {
                                                        setSelectedGroups([]);
                                                        setSelectedProducts([]);
                                                        setPublishedFilter('unpublished');
                                                    }}
                                                >
                                                    Clear Filters
                                                </button>
                                            )}
                                        </div>
                                    </Menu.Items>
                                </Menu>
                                <Menu className="relative" style={{ zIndex: 9999 }}>
                                    <Menu.Button className="relative inline-flex min-w-[120px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm whitespace-nowrap">
                                        <Lucide
                                            icon="GripVertical"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        Freeze Columns
                                    </Menu.Button>
                                    <Menu.Items className="w-56" style={{ zIndex: 9999 }}>
                                        <div className="p-3">
                                            <div className="text-xs font-medium mb-2">Select columns to freeze</div>
                                            <div className="space-y-2">
                                                {availableColumns.map((col) => (
                                                    <label
                                                        key={col.key}
                                                        className="flex items-center cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={frozenColumns.includes(col.key)}
                                                            onChange={() => toggleFrozenColumn(col.key)}
                                                            className="mr-2 rounded border-slate-300 text-primary focus:ring-primary"
                                                        />
                                                        <span className="text-sm">{col.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </Menu.Items>
                                </Menu>
                            </div>
                        </div>
                        {dataLoading && (
                            <div className="mt-3.5 box box--stacked p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Lucide icon="Loader2" className="w-4 h-4 animate-spin text-primary" />
                                    <span className="text-sm font-medium">Loading Master Data</span>
                                </div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-slate-600 dark:text-slate-400">
                                        {fetchProgress && fetchProgress.total > 0
                                            ? `${fetchProgress.loaded.toLocaleString()} of ${fetchProgress.total.toLocaleString()} records`
                                            : 'Preparing records...'}
                                    </span>
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        {fetchProgressPercent}%
                                    </span>
                                </div>
                                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
                                    <div
                                        className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
                                        style={{ width: `${fetchProgressPercent}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}
                        <div className="mt-3.5" style={{ width: '100%', maxWidth: '100%', overflow: 'visible', paddingLeft: '0', paddingRight: '0' }}>
                            <div className="flex flex-col box box--stacked" style={{ width: '100%', maxWidth: '100%', overflow: 'visible', marginLeft: '0', marginRight: '0' }}>
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div>
                                        <form onSubmit={handleSearch}>
                                            <div className="relative">
                                                <Lucide
                                                    icon="Search"
                                                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                                />
                                                <FormInput
                                                    type="text"
                                                    placeholder="Search..."
                                                    className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                            </div>
                                        </form>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 sm:ml-auto">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={handleFileSelect}
                                            style={{ display: 'none' }}
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            className="w-full sm:w-auto"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isImporting}
                                        >
                                            <Lucide
                                                icon={isImporting ? "Loader2" : "FileUp"}
                                                className={`stroke-[1.3] w-4 h-4 mr-2 ${isImporting ? 'animate-spin' : ''}`}
                                            />
                                            {isImporting ? 'Importing...' : 'Import from Excel'}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            className="w-full sm:w-auto"
                                            onClick={handleExportToExcel}
                                        >
                                            <Lucide
                                                icon="FileBarChart2"
                                                className="stroke-[1.3] w-4 h-4 mr-2"
                                            />
                                            Export to Excel
                                        </Button>
                                    </div>
                                </div>
                                <div className="relative overflow-auto overflow-x-auto" style={{ maxHeight: 'calc(100vh - 400px)', zIndex: 1, width: '100%', maxWidth: '100%' }}>
                                    {/* Overlay to hide gap on the left when scrolling */}
                                    <div 
                                        className="absolute left-0 top-0 bottom-0 bg-white dark:bg-darkmode-600 z-[100] pointer-events-none"
                                        style={{ width: '16px' }}
                                    />
                                    <Table className="border-b border-slate-200/60 text-xs" style={{ tableLayout: 'fixed', width: `${totalTableWidth}px`, borderCollapse: 'separate', borderSpacing: 0 }}>
                                        <Table.Thead className="text-xs sticky top-0 z-[50] shadow-sm border-b border-slate-200/60">
                                            <Table.Tr>
                                                {/* Published Status Column - First Column */}
                                                <Table.Td 
                                                    className="py-1.5 bg-blue-100 dark:bg-blue-900 text-center relative"
                                                    style={{
                                                        ...getStickyStyle('published', { 
                                                            width: columnWidths.published || 80, 
                                                            paddingRight: '4px',
                                                            paddingLeft: '4px'
                                                        }, true),
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1px',
                                                        borderRightColor: '#cbd5e1'
                                                    }}
                                                    rowSpan={2}
                                                >
                                                    Status
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-200 dark:hover:bg-darkmode-700 select-none bg-blue-100 dark:bg-blue-900 relative"
                                                    onClick={() => handleSort('createdAt')}
                                                    style={{
                                                        ...getStickyStyle('date', { 
                                                            width: columnWidths.date, 
                                                            paddingRight: '8px'
                                                        }, true),
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1px',
                                                        borderRightColor: '#cbd5e1'
                                                    }}
                                                    rowSpan={2}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Date
                                                        <Lucide 
                                                            icon={sortField === 'createdAt' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'createdAt' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                    <div 
                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                        onMouseDown={(e) => handleResizeStart('date', e)}
                                                    />
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-200 dark:hover:bg-darkmode-700 select-none bg-blue-100 dark:bg-blue-900 relative"
                                                    onClick={() => handleSort('dateByWeek')}
                                                    style={getStickyStyle('dateByWeek', { 
                                                        width: columnWidths.dateByWeek, 
                                                        paddingRight: '4px', 
                                                        paddingLeft: '4px',
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1.5px',
                                                        borderRightColor: '#64748b'
                                                    }, true)}
                                                    rowSpan={2}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Date by Week
                                                        <Lucide 
                                                            icon={sortField === 'dateByWeek' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'dateByWeek' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                    <div 
                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                        onMouseDown={(e) => handleResizeStart('dateByWeek', e)}
                                                    />
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-200 dark:hover:bg-darkmode-700 select-none bg-blue-100 dark:bg-blue-900 relative"
                                                    onClick={() => handleSort('region')}
                                                    style={{
                                                        ...getStickyStyle('region', { 
                                                            width: columnWidths.region, 
                                                            paddingRight: '8px', 
                                                            paddingLeft: '4px'
                                                        }, true),
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1px',
                                                        borderRightColor: '#cbd5e1'
                                                    }}
                                                    rowSpan={2}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Region
                                                        <Lucide 
                                                            icon={sortField === 'region' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'region' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                    <div 
                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                        onMouseDown={(e) => handleResizeStart('region', e)}
                                                    />
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-200 dark:hover:bg-darkmode-700 select-none bg-blue-100 dark:bg-blue-900 relative"
                                                    onClick={() => handleSort('category')}
                                                    style={getStickyStyle('category', { 
                                                        width: columnWidths.category, 
                                                        paddingRight: '8px', 
                                                        paddingLeft: '4px',
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1.5px',
                                                        borderRightColor: '#64748b'
                                                    }, true)}
                                                    rowSpan={2}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Category
                                                        <Lucide 
                                                            icon={sortField === 'category' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'category' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                    <div 
                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                        onMouseDown={(e) => handleResizeStart('category', e)}
                                                    />
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-200 dark:hover:bg-darkmode-700 select-none bg-blue-100 dark:bg-blue-900 relative"
                                                    onClick={() => handleSort('group')}
                                                    style={getStickyStyle('group', { 
                                                        width: columnWidths.group, 
                                                        paddingRight: '8px', 
                                                        paddingLeft: '4px',
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1.5px',
                                                        borderRightColor: '#64748b'
                                                    }, true)}
                                                    rowSpan={2}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Group
                                                        <Lucide 
                                                            icon={sortField === 'group' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'group' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                    <div 
                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                        onMouseDown={(e) => handleResizeStart('group', e)}
                                                    />
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-200 dark:hover:bg-darkmode-700 select-none bg-blue-100 dark:bg-blue-900 relative"
                                                    onClick={() => handleSort('outlet')}
                                                    style={getStickyStyle('outlet', { 
                                                        width: columnWidths.outlet, 
                                                        paddingRight: '4px', 
                                                        paddingLeft: '4px',
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1.5px',
                                                        borderRightColor: '#64748b'
                                                    }, true)}
                                                    rowSpan={2}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Outlet
                                                        <Lucide 
                                                            icon={sortField === 'outlet' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'outlet' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                    <div 
                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                        onMouseDown={(e) => handleResizeStart('outlet', e)}
                                                    />
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-200 dark:hover:bg-darkmode-700 select-none bg-blue-100 dark:bg-blue-900 relative"
                                                    onClick={() => handleSort('team')}
                                                    style={getStickyStyle('team', { 
                                                        width: columnWidths.team, 
                                                        paddingLeft: '4px',
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1.5px',
                                                        borderRightColor: '#64748b'
                                                    }, true)}
                                                    rowSpan={2}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Team
                                                        <Lucide 
                                                            icon={sortField === 'team' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'team' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                </Table.Td>
                                                {filteredUniqueProducts.map((product, productIndex) => (
                                                    <Table.Td 
                                                        key={product.id}
                                                        className={`py-1.5 bg-green-100 dark:bg-green-900 relative text-center border-solid ${
                                                            productIndex === 0 ? '' : 'border-l-2 border-l-slate-300 dark:border-l-slate-600'
                                                        } border-r-2 border-r-slate-300 dark:border-r-slate-600`}
                                                        colSpan={6}
                                                        style={{ 
                                                            width: getProductHeaderWidth(product.id),
                                                            paddingRight: '4px', 
                                                            paddingLeft: '4px',
                                                            borderLeftColor: productIndex === 0 ? undefined : '#cbd5e1',
                                                            borderRightColor: '#cbd5e1',
                                                            borderLeftStyle: productIndex === 0 ? undefined : 'solid',
                                                            borderRightStyle: 'solid',
                                                            borderLeftWidth: productIndex === 0 ? undefined : '2px',
                                                            borderRightWidth: '2px'
                                                        }}
                                                    >
                                                        <div className="font-medium">{product.name}</div>
                                                        <div 
                                                            className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                            onMouseDown={(e) => handleResizeStart(`product_${product.id}`, e)}
                                                        />
                                                    </Table.Td>
                                                ))}
                                            </Table.Tr>
                                            <Table.Tr>
                                                {filteredUniqueProducts.map((product, productIndex) => (
                                                    <React.Fragment key={product.id}>
                                                        <Table.Td 
                                                            className={`py-1.5 bg-green-100 dark:bg-green-900 relative border-l-2 border-solid text-center ${
                                                                productIndex === 0 ? '' : 'border-l-slate-300 dark:border-l-slate-600'
                                                            }`}
                                                            style={{ 
                                                                width: columnWidths[`product_${product.id}_opening`] || 18, 
                                                                paddingRight: '4px', 
                                                                paddingLeft: '4px',
                                                                borderLeftColor: productIndex === 0 ? undefined : '#cbd5e1',
                                                                borderLeftStyle: 'solid',
                                                                borderLeftWidth: productIndex === 0 ? undefined : '2px',
                                                                borderRightStyle: 'dotted',
                                                                borderRightWidth: '1px',
                                                                borderRightColor: '#cbd5e1'
                                                            }}
                                                        >
                                                            Opening
                                                            <div 
                                                                className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                                onMouseDown={(e) => handleResizeStart(`product_${product.id}_opening`, e)}
                                                            />
                                                        </Table.Td>
                                                        <Table.Td 
                                                            className="py-1.5 bg-green-100 dark:bg-green-900 relative border-solid text-center"
                                                            style={{ 
                                                                width: columnWidths[`product_${product.id}_closing`] || 36, 
                                                                paddingRight: '4px', 
                                                                paddingLeft: '4px',
                                                                borderRightStyle: 'dotted',
                                                                borderRightWidth: '1px',
                                                                borderRightColor: '#cbd5e1'
                                                            }}
                                                        >
                                                            Closing
                                                            <div 
                                                                className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                                onMouseDown={(e) => handleResizeStart(`product_${product.id}_closing`, e)}
                                                            />
                                                        </Table.Td>
                                                        <Table.Td 
                                                            className="py-1.5 bg-green-100 dark:bg-green-900 relative border-solid text-center"
                                                            style={{ 
                                                                width: columnWidths[`product_${product.id}_sales_carton`] || 36, 
                                                                paddingRight: '4px', 
                                                                paddingLeft: '4px',
                                                                borderRightStyle: 'dotted',
                                                                borderRightWidth: '1px',
                                                                borderRightColor: '#cbd5e1'
                                                            }}
                                                        >
                                                            Sales (carton)
                                                            <div 
                                                                className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                                onMouseDown={(e) => handleResizeStart(`product_${product.id}_sales_carton`, e)}
                                                            />
                                                        </Table.Td>
                                                        <Table.Td 
                                                            className="py-1.5 bg-green-100 dark:bg-green-900 relative border-solid"
                                                            style={{ 
                                                                width: columnWidths[`product_${product.id}_hector_litre`] || 60, 
                                                                paddingRight: '4px', 
                                                                paddingLeft: '4px',
                                                                borderRightStyle: 'dotted',
                                                                borderRightWidth: '1px',
                                                                borderRightColor: '#cbd5e1'
                                                            }}
                                                        >
                                                            Hecto Litre
                                                            <div 
                                                                className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                                onMouseDown={(e) => handleResizeStart(`product_${product.id}_hector_litre`, e)}
                                                            />
                                                        </Table.Td>
                                                        <Table.Td 
                                                            className="py-1.5 bg-green-100 dark:bg-green-900 relative border-solid text-center"
                                                            style={{ 
                                                                width: columnWidths[`product_${product.id}_price`] || 60, 
                                                                paddingRight: '4px', 
                                                                paddingLeft: '4px',
                                                                borderRightStyle: 'dotted',
                                                                borderRightWidth: '1px',
                                                                borderRightColor: '#cbd5e1'
                                                            }}
                                                        >
                                                            Price
                                                            <div 
                                                                className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                                                                onMouseDown={(e) => handleResizeStart(`product_${product.id}_price`, e)}
                                                            />
                                                        </Table.Td>
                                                        <Table.Td 
                                                            className="py-1.5 bg-green-100 dark:bg-green-900 border-r-2 border-r-slate-300 dark:border-r-slate-600 border-solid text-center"
                                                            style={{ 
                                                                width: columnWidths[`product_${product.id}_sales_rm`] || 78, 
                                                                paddingRight: '4px',
                                                                paddingLeft: '4px',
                                                                borderRightColor: '#cbd5e1',
                                                                borderRightStyle: 'solid',
                                                                borderRightWidth: '2px'
                                                            }}
                                                        >
                                                            Sales (RM)
                                                        </Table.Td>
                                                    </React.Fragment>
                                                ))}
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {paginatedGroupedData.map((group, groupIndex) => {
                                                // Use the first item in the group for static columns (they should all be the same)
                                                const firstItem = group[0];
                                                // Create a map of productId -> item for quick lookup
                                                const productMap = new Map<string, HeinekenPriceData>();
                                                group.forEach(item => {
                                                    if (item.productId) {
                                                        productMap.set(item.productId, item);
                                                    }
                                                });
                                                
                                                return (
                                                    <Table.Tr key={`${firstItem.userId || 'no-user'}_${firstItem.createdAt ? dayjs(firstItem.createdAt).format('YYYY-MM-DD') : ''}_${firstItem.outlet?.id || 'no-outlet'}_${groupIndex}`} className="[&_td]:last:border-b-0">
                                                        {/* Published Status Indicator - First Column */}
                                                        <Table.Td className="py-1.5 border-dashed bg-white dark:bg-darkmode-600 text-xs text-center" style={getStickyStyle('published', { 
                                                            width: columnWidths.published || 80, 
                                                            paddingRight: '4px',
                                                            paddingLeft: '4px',
                                                            borderRightStyle: 'dotted',
                                                            borderRightWidth: '1px',
                                                            borderRightColor: '#94a3b8'
                                                        })}>
                                                            {firstItem.published ? (
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <div className="w-3 h-3 rounded-full bg-green-500" title="Published"></div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const recordIds = group.map(item => item.id);
                                                                            // Each group represents 1 row, so count as 1 record
                                                                            if (window.confirm(`Unpublish 1 record to allow editing?`)) {
                                                                                handleUnpublish(recordIds);
                                                                            }
                                                                        }}
                                                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                                                        title="Unpublish to edit"
                                                                    >
                                                                        Unpublish
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="w-3 h-3 rounded-full bg-red-500 mx-auto" title="Unpublished"></div>
                                                            )}
                                                        </Table.Td>
                                                        <Table.Td className="py-1.5 border-dashed bg-white dark:bg-darkmode-600 text-xs" style={getStickyStyle('date', { 
                                                            width: columnWidths.date, 
                                                            paddingRight: '8px',
                                                            borderRightStyle: 'dotted',
                                                            borderRightWidth: '1px',
                                                            borderRightColor: '#94a3b8'
                                                        })}>
                                                            {formatDate(firstItem.createdAt)}
                                                        </Table.Td>
                                                        <Table.Td className="py-1.5 border-dashed bg-white dark:bg-darkmode-600 text-xs" style={getStickyStyle('dateByWeek', { 
                                                            width: columnWidths.dateByWeek, 
                                                            paddingRight: '4px', 
                                                            paddingLeft: '4px',
                                                            borderRightStyle: 'dotted',
                                                            borderRightWidth: '1px',
                                                            borderRightColor: '#94a3b8'
                                                        })}>
                                                            {formatDateByWeek(firstItem.createdAt)}
                                                        </Table.Td>
                                                        <Table.Td className="py-1.5 border-dashed bg-white dark:bg-darkmode-600 text-xs" style={getStickyStyle('region', { 
                                                            width: columnWidths.region, 
                                                            paddingRight: '8px', 
                                                            paddingLeft: '4px',
                                                            borderRightStyle: 'dotted',
                                                            borderRightWidth: '1px',
                                                            borderRightColor: '#94a3b8'
                                                        })}>
                                                            {firstItem.regionName || '-'}
                                                        </Table.Td>
                                                        <Table.Td className="py-1.5 border-dashed bg-white dark:bg-darkmode-600 text-xs" style={getStickyStyle('category', { 
                                                            width: columnWidths.category, 
                                                            paddingRight: '8px', 
                                                            paddingLeft: '4px',
                                                            borderRightStyle: 'dotted',
                                                            borderRightWidth: '1px',
                                                            borderRightColor: '#94a3b8'
                                                        })}>
                                                            {firstItem.outlet?.outletCategoryRel?.catName || '-'}
                                                        </Table.Td>
                                                        <Table.Td className="py-1.5 border-dashed bg-white dark:bg-darkmode-600 text-xs" style={getStickyStyle('group', { 
                                                            width: columnWidths.group, 
                                                            paddingRight: '8px', 
                                                            paddingLeft: '4px',
                                                            borderRightStyle: 'dotted',
                                                            borderRightWidth: '1px',
                                                            borderRightColor: '#94a3b8'
                                                        })}>
                                                            {firstItem.outlet?.outletGroupRel?.groupName || '-'}
                                                        </Table.Td>
                                                        <Table.Td className="py-1.5 border-dashed bg-white dark:bg-darkmode-600 text-xs" style={getStickyStyle('outlet', { 
                                                            width: columnWidths.outlet, 
                                                            paddingRight: '4px', 
                                                            paddingLeft: '4px',
                                                            borderRightStyle: 'dotted',
                                                            borderRightWidth: '1px',
                                                            borderRightColor: '#94a3b8'
                                                        })}>
                                                            {firstItem.outlet?.name || '-'}
                                                        </Table.Td>
                                                        <Table.Td className="py-1.5 border-dashed bg-white dark:bg-darkmode-600 text-xs" style={getStickyStyle('team', { 
                                                            width: columnWidths.team, 
                                                            paddingLeft: '4px',
                                                            borderRightStyle: 'dotted',
                                                            borderRightWidth: '1px',
                                                            borderRightColor: '#94a3b8'
                                                        })}>
                                                            {firstItem.team?.name || '-'}
                                                        </Table.Td>
                                                        {uniqueProducts.map((product, productIndex) => {
                                                            const productItem = productMap.get(product.id);
                                                            const productKey = `product_${product.id}`;
                                                            return (
                                                                <React.Fragment key={product.id}>
                                                                    <Table.Td 
                                                                        className={`py-1.5 dark:bg-darkmode-600 text-xs border-l-2 border-solid text-center ${
                                                                            productItem && isCellEdited(productItem.id, product.id, 'opening') 
                                                                                ? 'bg-yellow-100 dark:bg-yellow-900' 
                                                                                : productItem && isCellSaved(productItem.id, product.id, 'opening')
                                                                                ? 'bg-green-300 dark:bg-green-800'
                                                                                : ''
                                                                        } ${productIndex === 0 ? '' : 'border-l-slate-300 dark:border-l-slate-600'}`}
                                                                        style={{ 
                                                                            width: columnWidths[`${productKey}_opening`] || 18, 
                                                                            paddingRight: '4px', 
                                                                            paddingLeft: '4px',
                                                                            borderLeftColor: productIndex === 0 ? undefined : '#cbd5e1',
                                                                            borderLeftStyle: 'solid',
                                                                            borderLeftWidth: productIndex === 0 ? undefined : '2px',
                                                                            borderRightStyle: 'dotted',
                                                                            borderRightWidth: '1px',
                                                                            borderRightColor: '#cbd5e1'
                                                                        }}
                                                                    >
                                                                        {/* Opening - editable */}
                                                                        {productItem ? (
                                                                            <FormInput
                                                                                id={`input_${productItem.id}_${product.id}_opening`}
                                                                                type="number"
                                                                                step="1"
                                                                                min="0"
                                                                                disabled={productItem.published}
                                                                                className={`text-xs text-center p-0 border-0 bg-transparent w-full focus:border focus:border-blue-500 focus:bg-white dark:focus:bg-darkmode-600 focus:rounded focus:px-1 focus:py-0.5 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield] ${productItem.published ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                                style={{ 
                                                                                    WebkitAppearance: 'textfield',
                                                                                    MozAppearance: 'textfield'
                                                                                }}
                                                                                value={getInputValue(productItem, product.id, 'opening')}
                                                                                onChange={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        handleOpeningChange(productItem, product.id, e.target.value);
                                                                                    }
                                                                                }}
                                                                                onBlur={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        const cellKey = getCellKey(productItem.id, product.id, 'opening');
                                                                                        // Clear raw input on blur
                                                                                        const updatedRawInput = new Map(rawInputValues);
                                                                                        updatedRawInput.delete(cellKey);
                                                                                        setRawInputValues(updatedRawInput);
                                                                                        
                                                                                        const value = parseFloat(e.target.value);
                                                                                        if (isNaN(value) || value < 0) {
                                                                                            // Reset to last valid value
                                                                                            const validValue = getCellValue(productItem, product.id, 'opening');
                                                                                            if (validValue !== null && validValue !== undefined) {
                                                                                                e.target.value = Math.round(validValue).toString();
                                                                                            } else {
                                                                                                e.target.value = '';
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                onFocus={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        e.target.select();
                                                                                    }
                                                                                }}
                                                                                title={productItem.published ? 'This record is published. Unpublish to edit.' : ''}
                                                                            />
                                                                        ) : '-'}
                                                                    </Table.Td>
                                                                    <Table.Td 
                                                                        className={`py-1.5 dark:bg-darkmode-600 text-xs border-solid text-center ${
                                                                            productItem && isCellEdited(productItem.id, product.id, 'closing') 
                                                                                ? 'bg-yellow-100 dark:bg-yellow-900' 
                                                                                : productItem && isCellSaved(productItem.id, product.id, 'closing')
                                                                                ? 'bg-green-300 dark:bg-green-800'
                                                                                : ''
                                                                        }`}
                                                                        style={{ 
                                                                            width: columnWidths[`${productKey}_closing`] || 36, 
                                                                            paddingRight: '4px', 
                                                                            paddingLeft: '4px',
                                                                            borderRightStyle: 'dotted',
                                                                            borderRightWidth: '1px',
                                                                            borderRightColor: '#cbd5e1'
                                                                        }}
                                                                    >
                                                                        {/* Closing - editable */}
                                                                        {productItem ? (
                                                                            <FormInput
                                                                                id={`input_${productItem.id}_${product.id}_closing`}
                                                                                type="number"
                                                                                step="1"
                                                                                min="0"
                                                                                disabled={productItem.published}
                                                                                className={`text-xs text-center p-0 border-0 bg-transparent w-full focus:border focus:border-blue-500 focus:bg-white dark:focus:bg-darkmode-600 focus:rounded focus:px-1 focus:py-0.5 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield] ${productItem.published ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                                style={{ 
                                                                                    WebkitAppearance: 'textfield',
                                                                                    MozAppearance: 'textfield'
                                                                                }}
                                                                                value={getInputValue(productItem, product.id, 'closing')}
                                                                                onChange={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        handleClosingChange(productItem, product.id, e.target.value);
                                                                                    }
                                                                                }}
                                                                                onBlur={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        const cellKey = getCellKey(productItem.id, product.id, 'closing');
                                                                                        // Clear raw input on blur
                                                                                        const updatedRawInput = new Map(rawInputValues);
                                                                                        updatedRawInput.delete(cellKey);
                                                                                        setRawInputValues(updatedRawInput);
                                                                                        
                                                                                        const value = parseFloat(e.target.value);
                                                                                        if (isNaN(value) || value < 0) {
                                                                                            // Reset to last valid value
                                                                                            const validValue = getCellValue(productItem, product.id, 'closing');
                                                                                            if (validValue !== null && validValue !== undefined) {
                                                                                                e.target.value = Math.round(validValue).toString();
                                                                                            } else {
                                                                                                e.target.value = '';
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                onFocus={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        e.target.select();
                                                                                    }
                                                                                }}
                                                                                title={productItem.published ? 'This record is published. Unpublish to edit.' : ''}
                                                                            />
                                                                        ) : '-'}
                                                                    </Table.Td>
                                                                    <Table.Td 
                                                                        className={`py-1.5 dark:bg-darkmode-600 text-xs border-solid text-center ${
                                                                            productItem && isCellEdited(productItem.id, product.id, 'qty') 
                                                                                ? 'bg-yellow-100 dark:bg-yellow-900' 
                                                                                : productItem && isCellSaved(productItem.id, product.id, 'qty')
                                                                                ? 'bg-green-300 dark:bg-green-800'
                                                                                : ''
                                                                        }`}
                                                                        style={{ 
                                                                            width: columnWidths[`${productKey}_sales_carton`] || 36, 
                                                                            paddingRight: '4px', 
                                                                            paddingLeft: '4px',
                                                                            borderRightStyle: 'dotted',
                                                                            borderRightWidth: '1px',
                                                                            borderRightColor: '#cbd5e1'
                                                                        }}
                                                                    >
                                                                        {/* Sales (carton) - editable */}
                                                                        {productItem ? (
                                                                            <FormInput
                                                                                id={`input_${productItem.id}_${product.id}_qty`}
                                                                                type="number"
                                                                                step="1"
                                                                                min="0"
                                                                                disabled={productItem.published}
                                                                                className={`text-xs text-center p-0 border-0 bg-transparent w-full focus:border focus:border-blue-500 focus:bg-white dark:focus:bg-darkmode-600 focus:rounded focus:px-1 focus:py-0.5 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield] ${productItem.published ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                                style={{ 
                                                                                    WebkitAppearance: 'textfield',
                                                                                    MozAppearance: 'textfield'
                                                                                }}
                                                                                value={getInputValue(productItem, product.id, 'qty')}
                                                                                onChange={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        handleSalesCartonChange(productItem, product.id, e.target.value);
                                                                                    }
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        handleTabKey(e, productItem.id, product.id, 'qty', e.shiftKey ? 'prev' : 'next');
                                                                                    }
                                                                                }}
                                                                                onBlur={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        const cellKey = getCellKey(productItem.id, product.id, 'qty');
                                                                                        // Clear raw input on blur
                                                                                        const updatedRawInput = new Map(rawInputValues);
                                                                                        updatedRawInput.delete(cellKey);
                                                                                        setRawInputValues(updatedRawInput);
                                                                                        
                                                                                        const value = parseFloat(e.target.value);
                                                                                        if (isNaN(value) || value < 0) {
                                                                                            // Reset to last valid value
                                                                                            const validValue = getCellValue(productItem, product.id, 'qty');
                                                                                            if (validValue !== null && validValue !== undefined) {
                                                                                                e.target.value = Math.round(validValue).toString();
                                                                                            } else {
                                                                                                e.target.value = '';
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                onFocus={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        e.target.select();
                                                                                    }
                                                                                }}
                                                                                title={productItem.published ? 'This record is published. Unpublish to edit.' : ''}
                                                                            />
                                                                        ) : '-'}
                                                                    </Table.Td>
                                                                    <Table.Td 
                                                                        className="py-1.5 dark:bg-darkmode-600 text-xs border-solid text-right" 
                                                                        style={{ 
                                                                            width: columnWidths[`${productKey}_hector_litre`] || 60, 
                                                                            paddingRight: '4px', 
                                                                            paddingLeft: '4px',
                                                                            borderRightStyle: 'dotted',
                                                                            borderRightWidth: '1px',
                                                                            borderRightColor: '#cbd5e1'
                                                                        }}
                                                                    >
                                                                        {productItem ? (() => {
                                                                            const salesCarton = getCellValue(productItem, product.id, 'qty');
                                                                            const productData = uniqueProducts.find(p => p.id === product.id);
                                                                            const grossWeightPerUOM = productData?.grossWeightPerUOM;
                                                                            
                                                                            if (salesCarton !== null && salesCarton !== undefined && grossWeightPerUOM !== null && grossWeightPerUOM !== undefined) {
                                                                                const hectoLitre = salesCarton * grossWeightPerUOM;
                                                                                return hectoLitre.toFixed(4);
                                                                            }
                                                                            return '-';
                                                                        })() : '-'}
                                                                    </Table.Td>
                                                                    <Table.Td 
                                                                        className={`py-1.5 dark:bg-darkmode-600 text-xs border-solid text-right ${
                                                                            productItem && isCellEdited(productItem.id, product.id, 'price') 
                                                                                ? 'bg-yellow-100 dark:bg-yellow-900' 
                                                                                : productItem && isCellSaved(productItem.id, product.id, 'price')
                                                                                ? 'bg-green-300 dark:bg-green-800'
                                                                                : ''
                                                                        }`}
                                                                        style={{ 
                                                                            width: columnWidths[`${productKey}_price`] || 60, 
                                                                            paddingRight: '4px', 
                                                                            paddingLeft: '4px',
                                                                            borderRightStyle: 'dotted',
                                                                            borderRightWidth: '1px',
                                                                            borderRightColor: '#cbd5e1'
                                                                        }}
                                                                    >
                                                                        {productItem ? (
                                                                            <FormInput
                                                                                id={`input_${productItem.id}_${product.id}_price`}
                                                                                type="text"
                                                                                disabled={productItem.published}
                                                                                className={`text-xs text-right p-0 border-0 bg-transparent w-full focus:border focus:border-blue-500 focus:bg-white dark:focus:bg-darkmode-600 focus:rounded focus:px-1 focus:py-0.5 ${productItem.published ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                                value={getInputValue(productItem, product.id, 'price')}
                                                                                onChange={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        // Only allow numbers and decimal point
                                                                                        const value = e.target.value;
                                                                                        // Allow empty, numbers, single decimal point, and numbers with decimal point
                                                                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                                                            handlePriceChange(productItem, product.id, value);
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        handleTabKey(e, productItem.id, product.id, 'price', e.shiftKey ? 'prev' : 'next');
                                                                                    }
                                                                                }}
                                                                                onBlur={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        const cellKey = getCellKey(productItem.id, product.id, 'price');
                                                                                        
                                                                                        const value = parseFloat(e.target.value);
                                                                                        if (isNaN(value) || value < 0) {
                                                                                            // Reset to last valid value
                                                                                            const validValue = getCellValue(productItem, product.id, 'price');
                                                                                            if (validValue !== null && validValue !== undefined) {
                                                                                                e.target.value = validValue.toFixed(2);
                                                                                            } else {
                                                                                                e.target.value = '';
                                                                                            }
                                                                                        } else {
                                                                                            // Format to 2 decimal places on blur
                                                                                            e.target.value = value.toFixed(2);
                                                                                            // Update the edited value with the formatted value
                                                                                            handlePriceChange(productItem, product.id, e.target.value);
                                                                                        }
                                                                                        
                                                                                        // Clear raw input on blur (after formatting)
                                                                                        const updatedRawInput = new Map(rawInputValues);
                                                                                        updatedRawInput.delete(cellKey);
                                                                                        setRawInputValues(updatedRawInput);
                                                                                    }
                                                                                }}
                                                                                onFocus={(e) => {
                                                                                    if (!productItem.published) {
                                                                                        e.target.select();
                                                                                    }
                                                                                }}
                                                                                title={productItem.published ? 'This record is published. Unpublish to edit.' : ''}
                                                                            />
                                                                        ) : '-'}
                                                                    </Table.Td>
                                                                    <Table.Td 
                                                                        className="py-1.5 dark:bg-darkmode-600 text-xs border-r-2 border-r-slate-300 dark:border-r-slate-600 border-solid text-right"
                                                                        style={{ 
                                                                            width: columnWidths[`${productKey}_sales_rm`] || 78, 
                                                                            paddingRight: '4px',
                                                                            paddingLeft: '4px',
                                                                            borderRightColor: '#cbd5e1',
                                                                            borderRightStyle: 'solid',
                                                                            borderRightWidth: '2px'
                                                                        }}
                                                                    >
                                                                        {/* Sales (RM) - read-only, auto-calculated from qty × price */}
                                                                        {productItem ? (
                                                                            (() => {
                                                                                const qty = getCellValue(productItem, product.id, 'qty');
                                                                                const calculatedTotal = calculateSalesRM(productItem, product.id);
                                                                                
                                                                                // If qty is null, Total (RM) should be null (don't show stored total)
                                                                                if (qty === null || qty === undefined) {
                                                                                    return '-';
                                                                                }
                                                                                
                                                                                // Otherwise, use calculated total or fall back to stored total
                                                                                const displayValue = calculatedTotal !== null 
                                                                                    ? calculatedTotal 
                                                                                    : getCellValue(productItem, product.id, 'total');
                                                                                return displayValue !== null && displayValue !== undefined
                                                                                    ? displayValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                                                    : '-';
                                                                            })()
                                                                        ) : '-'}
                                                                    </Table.Td>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </Table.Tr>
                                                );
                                            })}
                                        </Table.Tbody>
                                        <tfoot>
                                            <Table.Tr>
                                                {/* Published Status Column - First Column (matches header) */}
                                                <Table.Td 
                                                    className="py-1.5 bg-slate-50 dark:bg-darkmode-700 text-xs font-semibold"
                                                    style={getStickyStyle('published', { 
                                                        width: columnWidths.published || 80, 
                                                        paddingRight: '4px',
                                                        paddingLeft: '4px',
                                                        borderRightStyle: 'dotted',
                                                        borderRightWidth: '1px',
                                                        borderRightColor: '#cbd5e1'
                                                    }, false)}
                                                >
                                                    {/* Empty for status column */}
                                                </Table.Td>
                                                {/* Static columns - render all columns to maintain structure, matching header padding */}
                                                {availableColumns.map((col) => {
                                                    const isFrozen = frozenColumns.includes(col.key);
                                                    // Match header padding exactly
                                                    let paddingStyle: React.CSSProperties = {};
                                                    if (col.key === 'date') {
                                                        paddingStyle = { paddingRight: '8px' };
                                                    } else if (col.key === 'dateByWeek') {
                                                        paddingStyle = { paddingRight: '4px', paddingLeft: '4px' };
                                                    } else if (col.key === 'region') {
                                                        paddingStyle = { paddingRight: '8px', paddingLeft: '4px' };
                                                    } else if (col.key === 'category') {
                                                        paddingStyle = { paddingRight: '8px', paddingLeft: '4px' };
                                                    } else if (col.key === 'group') {
                                                        paddingStyle = { paddingRight: '8px', paddingLeft: '4px' };
                                                    } else if (col.key === 'outlet') {
                                                        paddingStyle = { paddingRight: '4px', paddingLeft: '4px' };
                                                    } else if (col.key === 'team') {
                                                        paddingStyle = { paddingLeft: '4px' };
                                                    }
                                                    
                                                    const baseStyle = {
                                                        width: columnWidths[col.key] || 120,
                                                        ...paddingStyle,
                                                        borderRightStyle: 'dotted' as const,
                                                        borderRightWidth: col.key === 'date' ? '1px' : '1.5px',
                                                        borderRightColor: col.key === 'date' ? '#cbd5e1' : '#64748b'
                                                    };
                                                    
                                                    return (
                                                        <Table.Td 
                                                            key={col.key}
                                                            className="py-1.5 bg-slate-50 dark:bg-darkmode-700 text-xs font-semibold"
                                                            style={isFrozen ? getStickyStyle(col.key, baseStyle, false) : baseStyle}
                                                        >
                                                            {/* Empty for static columns */}
                                                        </Table.Td>
                                                    );
                                                })}
                                                {/* Product columns */}
                                                {filteredUniqueProducts.map((product, productIndex) => {
                                                    const productKey = `product_${product.id}`;
                                                    return (
                                                        <React.Fragment key={product.id}>
                                                            {/* Opening */}
                                                            <Table.Td 
                                                                className="py-1.5 bg-slate-50 dark:bg-darkmode-700 text-xs border-l-2 border-solid text-center"
                                                                style={{ 
                                                                    width: columnWidths[`${productKey}_opening`] || 18, 
                                                                    paddingRight: '4px', 
                                                                    paddingLeft: '4px',
                                                                    borderLeftColor: productIndex === 0 ? undefined : '#cbd5e1',
                                                                    borderLeftStyle: 'solid',
                                                                    borderLeftWidth: productIndex === 0 ? undefined : '2px',
                                                                    borderRightStyle: 'dotted',
                                                                    borderRightWidth: '1px',
                                                                    borderRightColor: '#cbd5e1'
                                                                }}
                                                            >
                                                                -
                                                            </Table.Td>
                                                            {/* Closing */}
                                                            <Table.Td 
                                                                className="py-1.5 bg-slate-50 dark:bg-darkmode-700 text-xs border-solid text-center"
                                                                style={{ 
                                                                    width: columnWidths[`${productKey}_closing`] || 36, 
                                                                    paddingRight: '4px', 
                                                                    paddingLeft: '4px',
                                                                    borderRightStyle: 'dotted',
                                                                    borderRightWidth: '1px',
                                                                    borderRightColor: '#cbd5e1'
                                                                }}
                                                            >
                                                                -
                                                            </Table.Td>
                                                            {/* Sales (carton) */}
                                                            <Table.Td 
                                                                className="py-1.5 bg-slate-50 dark:bg-darkmode-700 text-xs border-solid text-center"
                                                                style={{ 
                                                                    width: columnWidths[`${productKey}_sales_carton`] || 36, 
                                                                    paddingRight: '4px', 
                                                                    paddingLeft: '4px',
                                                                    borderRightStyle: 'dotted',
                                                                    borderRightWidth: '1px',
                                                                    borderRightColor: '#cbd5e1'
                                                                }}
                                                            >
                                                                -
                                                            </Table.Td>
                                                            {/* Hecto Litre */}
                                                            <Table.Td 
                                                                className="py-1.5 bg-slate-50 dark:bg-darkmode-700 text-xs border-solid text-right font-semibold"
                                                                style={{ 
                                                                    width: columnWidths[`${productKey}_hector_litre`] || 60, 
                                                                    paddingRight: '4px', 
                                                                    paddingLeft: '4px',
                                                                    borderRightStyle: 'dotted',
                                                                    borderRightWidth: '1px',
                                                                    borderRightColor: '#cbd5e1'
                                                                }}
                                                            >
                                                                {totalHectoLitreByProduct?.get(product.id) 
                                                                    ? totalHectoLitreByProduct.get(product.id)!.toFixed(4)
                                                                    : '-'}
                                                            </Table.Td>
                                                            {/* Price */}
                                                            <Table.Td 
                                                                className="py-1.5 bg-slate-50 dark:bg-darkmode-700 text-xs border-solid text-right font-semibold"
                                                                style={{ 
                                                                    width: columnWidths[`${productKey}_price`] || 60, 
                                                                    paddingRight: '4px', 
                                                                    paddingLeft: '4px',
                                                                    borderRightStyle: 'dotted',
                                                                    borderRightWidth: '1px',
                                                                    borderRightColor: '#cbd5e1'
                                                                }}
                                                            >
                                                                Total
                                                            </Table.Td>
                                                            {/* Sales (RM) - show total */}
                                                            <Table.Td 
                                                                className="py-1.5 bg-slate-50 dark:bg-darkmode-700 text-xs border-r-2 border-r-slate-300 dark:border-r-slate-600 border-solid text-right font-semibold cursor-pointer hover:bg-slate-100 dark:hover:bg-darkmode-500 transition-colors"
                                                                style={{ 
                                                                    width: columnWidths[`${productKey}_sales_rm`] || 78, 
                                                                    paddingRight: '4px',
                                                                    paddingLeft: '4px',
                                                                    borderRightColor: '#cbd5e1',
                                                                    borderRightStyle: 'solid',
                                                                    borderRightWidth: '2px'
                                                                }}
                                                                onClick={() => {
                                                                    // Check if any items in current view for this product are published
                                                                    const hasPublishedItems = paginatedGroupedData.some(group => 
                                                                        group.some(item => item.productId === product.id && item.published)
                                                                    );
                                                                    if (!hasPublishedItems) {
                                                                        handleTotalSalesRMClick(product.id);
                                                                    } else {
                                                                        alert('Cannot adjust published records. Please unpublish first.');
                                                                    }
                                                                }}
                                                            >
                                                                {totalSalesRMByProduct.get(product.id) 
                                                                    ? totalSalesRMByProduct.get(product.id)!.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                                    : '0.00'}
                                                            </Table.Td>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </Table.Tr>
                                        </tfoot>
                                    </Table>
                                </div>
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row sm:gap-x-3">
                                    <div className="text-sm text-slate-600 dark:text-slate-400 mr-4">
                                        {displayedTotal > 0 ? (
                                            <>
                                                Showing {displayedStartIndex + 1} to {displayedEndIndex} of {displayedTotal} records
                                            </>
                                        ) : (
                                            'No records found'
                                        )}
                                    </div>
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage > 1) handleFirstPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage > 1) handlePrevPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        {effectiveTotalPages > 0 && (
                                            <>
                                                {Array.from({ length: Math.min(effectiveTotalPages, 5) }).map((_, i) => {
                                                    let pageNum: number;
                                                    if (effectiveTotalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= effectiveTotalPages - 2) {
                                                        pageNum = effectiveTotalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <Pagination.Link
                                                            key={pageNum}
                                                            active={currentPage === pageNum}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setCurrentPage(pageNum);
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            {pageNum}
                                                        </Pagination.Link>
                                                    );
                                                })}
                                            </>
                                        )}
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage < effectiveTotalPages) handleNextPage();
                                            }}
                                            className={currentPage >= effectiveTotalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage < effectiveTotalPages) handleLastPage();
                                            }}
                                            className={currentPage >= effectiveTotalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                    </Pagination>
                                    {unpublishedCount > 0 && publishedFilter === 'unpublished' && (
                                        <Button 
                                            variant="primary" 
                                            className="sm:w-auto bg-green-600 hover:bg-green-700" 
                                            onClick={() => setShowPublishModal(true)}
                                            disabled={isPublishing}
                                        >
                                            <Lucide icon="Send" className="w-4 h-4 mr-2" />
                                            Publish ({unpublishedCount})
                                        </Button>
                                    )}
                                    {countDirectEdits > 0 && (
                                        <>
                                            <Button
                                                variant="primary"
                                                className="sm:w-auto"
                                                onClick={handleApplyChanges}
                                                disabled={isApplying}
                                            >
                                                {isApplying ? (
                                                    <>
                                                        <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                                                        Applying...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Lucide icon="Check" className="w-4 h-4 mr-2" />
                                                        Apply ({countDirectEdits})
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                className="sm:w-auto"
                                                onClick={handleResetChanges}
                                            >
                                                <Lucide icon="RotateCcw" className="w-4 h-4 mr-2" />
                                                Reset
                                            </Button>
                                        </>
                                    )}
                                    <FormSelect 
                                        className="sm:w-20 rounded-[0.5rem] ml-auto"
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                    >
                                        <option value={100}>100</option>
                                        <option value={250}>250</option>
                                        <option value={500}>500</option>
                                        <option value={1000}>1000</option>
                                    </FormSelect>
                                </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Publish Confirmation Modal */}
                    <Dialog open={showPublishModal} onClose={() => {
                        if (!isPublishing) {
                            setShowPublishModal(false);
                        }
                    }} size="lg">
                        <Dialog.Panel>
                            <Dialog.Title>
                                <h2 className="text-lg font-medium">Publish Records</h2>
                            </Dialog.Title>
                            <Dialog.Description>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                                            You are about to publish <strong>{unpublishedCount}</strong> record(s) to the public view.
                                        </p>
                                        {dateRange && (
                                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                                                <strong>Date Range:</strong> {dateRange}
                                            </p>
                                        )}
                                        <p className="text-sm text-red-600 dark:text-red-400 mt-3 font-medium">
                                            ⚠️ Once published, these records cannot be edited unless unpublished first.
                                        </p>
                                    </div>
                                </div>
                            </Dialog.Description>
                            <Dialog.Footer>
                                <Button
                                    variant="secondary"
                                    className="mr-2"
                                    onClick={() => setShowPublishModal(false)}
                                    disabled={isPublishing}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={handlePublish}
                                    disabled={isPublishing}
                                >
                                    {isPublishing ? (
                                        <>
                                            <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                                            Publishing...
                                        </>
                                    ) : (
                                        <>
                                            <Lucide icon="Send" className="w-4 h-4 mr-2" />
                                            Publish
                                        </>
                                    )}
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Panel>
                    </Dialog>
                    
                    {/* Import Progress Modal */}
                    <Dialog open={isImporting} onClose={() => {}} size="md">
                        <Dialog.Panel>
                            <Dialog.Title>
                                <h2 className="text-lg font-medium">Importing Excel File</h2>
                            </Dialog.Title>
                            <Dialog.Description>
                                <div className="space-y-4 py-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-slate-600 dark:text-slate-400">
                                            {importProgress.stage || 'Processing...'}
                                        </span>
                                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            {importProgress.percentage}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
                                        <div 
                                            className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
                                            style={{ width: `${importProgress.percentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="flex items-center justify-center pt-2">
                                        <Lucide icon="Loader2" className="w-5 h-5 animate-spin text-primary" />
                                    </div>
                                </div>
                            </Dialog.Description>
                        </Dialog.Panel>
                    </Dialog>

                    {/* Import Summary Modal */}
                    <Dialog open={showImportSummary} onClose={() => {
                        setShowImportSummary(false);
                        setImportResult(null);
                    }} size="lg">
                        <Dialog.Panel>
                            <Dialog.Title>
                                <h2 className="text-lg font-medium">Import Summary</h2>
                            </Dialog.Title>
                            <Dialog.Description>
                                <div className="space-y-4">
                                    {importResult && (
                                        <>
                                            <div className="flex items-center gap-4">
                                                {importResult.success > 0 && (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                                                            <Lucide icon="Check" className="w-4 h-4 text-green-600 dark:text-green-400" />
                                                        </div>
                                                        <span className="text-sm text-slate-600 dark:text-slate-400">{importResult.success} record(s) updated</span>
                                                    </div>
                                                )}
                                                {importResult.warnings > 0 && (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                                                            <Lucide icon="AlertTriangle" className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                                                        </div>
                                                        <span className="text-sm text-slate-600 dark:text-slate-400">{importResult.warnings} warning(s)</span>
                                                    </div>
                                                )}
                                                {importResult.errors > 0 && (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                                                            <span className="text-red-600 dark:text-red-400 font-semibold">{importResult.errors}</span>
                                                        </div>
                                                        <span className="text-sm text-slate-600 dark:text-slate-400">Error(s)</span>
                                                    </div>
                                                )}
                                            </div>
                                            {importResult.warningDetails.length > 0 && (
                                                <div className="mt-4">
                                                    <p className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-2">Warnings (Published records skipped):</p>
                                                    <div className="max-h-40 overflow-y-auto border border-yellow-200 dark:border-yellow-800 rounded p-3 bg-yellow-50 dark:bg-yellow-900/20">
                                                        <ul className="space-y-1 text-xs">
                                                            {importResult.warningDetails.slice(0, 10).map((warning, index) => (
                                                                <li key={index} className="text-yellow-700 dark:text-yellow-400">
                                                                    <strong>Row {warning.row}:</strong> {warning.message}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        {importResult.warningDetails.length > 10 && (
                                                            <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-2">
                                                                ... and {importResult.warningDetails.length - 10} more warning(s)
                                                            </p>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                                                        Published records cannot be updated. Unpublish them first if you need to edit.
                                                    </p>
                                                </div>
                                            )}
                                            {importResult.errorDetails.length > 0 && (
                                                <div className="mt-4">
                                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Error Details:</p>
                                                    <div className="max-h-60 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded p-3 bg-slate-50 dark:bg-darkmode-800">
                                                        <ul className="space-y-1 text-xs">
                                                            {importResult.errorDetails.map((error, index) => (
                                                                <li key={index} className="text-red-600 dark:text-red-400">
                                                                    <strong>Row {error.row}:</strong> {error.message}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                                                        Please correct the errors in your Excel file and re-import.
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Dialog.Description>
                            <Dialog.Footer>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        setShowImportSummary(false);
                                        setImportResult(null);
                                    }}
                                >
                                    Close
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Panel>
                    </Dialog>
                    
                    {/* Adjustment Modal */}
                    <Dialog open={showAdjustmentModal} onClose={() => {
                        if (!isApplying) {
                            setShowAdjustmentModal(false);
                            setSelectedProductForAdjustment(null);
                            setAdjustmentPercentage('');
                        }
                    }} size="lg">
                        <Dialog.Panel>
                            <Dialog.Title>
                                <h2 className="text-lg font-medium">
                                    {selectedProductForAdjustment?.name || 'Adjust Sales (RM)'}
                                </h2>
                            </Dialog.Title>
                            <Dialog.Description>
                                <div className="space-y-4">
                                    {/* Date Range */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Date Range:
                                        </label>
                                        <div className="text-sm text-slate-600 dark:text-slate-400">
                                            {selectedProductForAdjustment 
                                                ? getProductAdjustmentInfo(selectedProductForAdjustment.id).dateRange || 'N/A'
                                                : 'N/A'}
                                        </div>
                                    </div>

                                    {/* Current Total Sales */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Current Total Sales:
                                        </label>
                                        <div className="text-sm text-slate-600 dark:text-slate-400">
                                            {selectedProductForAdjustment 
                                                ? getProductAdjustmentInfo(selectedProductForAdjustment.id).currentTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '0.00'}
                                        </div>
                                    </div>

                                    {/* Adjustment Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Adjustment (+/- %):
                                        </label>
                                        <FormInput
                                            type="number"
                                            step="0.01"
                                            className="mt-1"
                                            placeholder="e.g., 10 for +10%, -5 for -5%"
                                            value={adjustmentPercentage}
                                            onChange={(e) => setAdjustmentPercentage(e.target.value)}
                                            disabled={isApplying}
                                        />
                                    </div>

                                    {/* Adjusted Total Sales */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Adjusted Total Sales:
                                        </label>
                                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                            {adjustedTotalSales !== null
                                                ? adjustedTotalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : '-'}
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Description>
                            <Dialog.Footer>
                                <Button
                                    variant="secondary"
                                    className="mr-2"
                                    onClick={() => {
                                        setShowAdjustmentModal(false);
                                        setSelectedProductForAdjustment(null);
                                        setAdjustmentPercentage('');
                                    }}
                                    disabled={isApplying}
                                >
                                    Close
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleApplyAdjustment}
                                    disabled={isApplying || !adjustmentPercentage || isNaN(parseFloat(adjustmentPercentage))}
                                >
                                    {isApplying ? (
                                        <>
                                            <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                                            Applying...
                                        </>
                                    ) : (
                                        <>
                                            <Lucide icon="Check" className="w-4 h-4 mr-2" />
                                            Apply
                                        </>
                                    )}
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Panel>
                    </Dialog>
                                    
            </RoleGate>
        </ProtectedRoute>
    );
}

